/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useWBL } from '../../context/WBLContext';
import { SC_COUNTIES } from '../scPaths';
import { 
  ArrowLeft, Search, Globe, ShieldCheck, ChevronDown, ChevronUp, 
  Users, Activity, Key, BookOpen, PlayCircle, Database, Palette, 
  Image as ImageIcon, Scale, Settings, Upload, Trash2, RefreshCw,
  Check, CheckCircle, HelpCircle, Sparkles, Info, X, GraduationCap, Briefcase, Heart, Bell,
  Sliders, ShieldAlert, Mail
} from 'lucide-react';
import { PortalConfigModal } from './PortalConfigModal';
import { ColorPalette, LogoSet, PageBanners, SUBCLUSTERS_MAP, UserRole, UserProfile, PersonaSection } from '../../types';
import { MetadataTabContent } from './MetadataTabContent';
import { CurriculumTabContent } from './CurriculumTabContent';
import { FoundationEditor } from './FoundationEditor';
import { EventsTabContent } from './EventsTabContent';
import { AccessTabContent } from './AccessTabContent';
import { CareerMappingTabContent } from './CareerMappingTabContent';
import { ConfigTabContent } from './ConfigTabContent';
import { UsersTabContent } from './UsersTabContent';
import { NotificationsTabContent } from './NotificationsTabContent';
import { ComplianceTabContent } from './ComplianceTabContent';
import { OpportunitiesAppsTabContent } from './OpportunitiesAppsTabContent';
import { StudentAvatar } from '../Common/StudentAvatar';
import { db, storage } from '../../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useInAppDialog, inAppConfirm, inAppAlert, inAppToast } from '../Common/InAppDialog';

// Custom reusable premium iOS-style Toggle Switch matching the screenshot
const ToggleSwitch: React.FC<{
  checked: boolean;
  onChange: (val: boolean) => void;
  id?: string;
}> = ({ checked, onChange, id }) => {
  return (
    <button
      id={id}
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
        checked ? 'bg-[#0094c8]' : 'bg-slate-200'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
};

// ==========================================
// PORTAL IDENTITY & BRAND WORKSPACE COMPONENT
// ==========================================
const IdentityTabContent: React.FC = () => {
  const { showAlert } = useInAppDialog();
  const { 
    tenants, 
    updateTenantBranding, 
    globalBranding, 
    updateGlobalBranding 
  } = useWBL();

  const [selectedSubTab, setSelectedSubTab] = useState<'GLOBAL' | 'REGIONAL'>('GLOBAL');
  const [selectedRegionId, setSelectedRegionId] = useState<string>(tenants[0]?.id || 'abbeville');
  const [regionSearchQuery, setRegionSearchQuery] = useState('');

  // Real Storage Upload State for Background Videos
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Load active branding values based on selected tab/region
  const isGlobal = selectedSubTab === 'GLOBAL';
  const activeRegion = useMemo(() => tenants.find(t => t.id === selectedRegionId), [tenants, selectedRegionId]);

  // Filter regional list for local search
  const filteredRegionOptions = useMemo(() => {
    return tenants.filter(t => t.id !== 'global' && (t.name || '').toLowerCase().includes((regionSearchQuery || '').toLowerCase()));
  }, [tenants, regionSearchQuery]);

  // Read current color palette
  const activePalette = useMemo(() => {
    if (isGlobal) {
      return globalBranding?.palette || {
        darkMain: '#0f172a',
        lightMain: '#0094c8',
        darkSecondary: '#1e293b',
        lightSecondary: '#94a3b8',
        background: '#f8fafc'
      };
    } else {
      return activeRegion?.palette || {
        darkMain: activeRegion?.primaryColor || '#0094c8',
        lightMain: '#0094c8',
        darkSecondary: activeRegion?.secondaryColor || '#4f46e5',
        lightSecondary: '#94a3b8',
        background: '#f8fafc'
      };
    }
  }, [isGlobal, globalBranding, activeRegion]);

  // Read current logos
  const activeLogos = useMemo(() => {
    if (isGlobal) {
      return globalBranding?.logos || {
        navigation: '',
        hero: '',
        secondary: '',
        sponsor1: '',
        sponsor2: ''
      };
    } else {
      return activeRegion?.logos || {
        navigation: '',
        hero: '',
        secondary: '',
        sponsor1: '',
        sponsor2: ''
      };
    }
  }, [isGlobal, globalBranding, activeRegion]);

  const handleColorChange = (key: keyof ColorPalette, hex: string) => {
    const updatedPalette = { ...activePalette, [key]: hex };
    if (isGlobal) {
      updateGlobalBranding({ palette: updatedPalette });
    } else {
      updateTenantBranding(selectedRegionId, { palette: updatedPalette });
    }
  };

  const handleLogoChange = (key: keyof LogoSet, value: string) => {
    const updatedLogos = { ...activeLogos, [key]: value };
    if (isGlobal) {
      updateGlobalBranding({ logos: updatedLogos });
    } else {
      updateTenantBranding(selectedRegionId, { logos: updatedLogos });
    }
  };

  const handleLogoUpload = (key: keyof LogoSet, file: File) => {
    // Compress and downscale uploaded images (except SVGs) to keep Firestore documents well within the 1MB limit
    if (file.type && file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxWidth = 450;
          const maxHeight = 160; // Logos are typically wide, limit height further
          let width = img.width;
          let height = img.height;

          // Scale within boundaries
          if (width / height > maxWidth / maxHeight) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            // Export as PNG to preserve transparency
            const compressedBase64 = canvas.toDataURL('image/png');
            handleLogoChange(key, compressedBase64);
          } else {
            handleLogoChange(key, event.target?.result as string);
          }
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    } else {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        handleLogoChange(key, base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGeneratePreset = (key: keyof LogoSet) => {
    const brandName = isGlobal ? 'Global Consortium' : `${activeRegion?.name || 'Regional'} WBL`;
    const label = key.toUpperCase();
    const color = activePalette.lightMain || '#0094c8';
    
    // Generate a beautiful inline vector SVG logo with text/branding matched
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 120" width="100%" height="100%">
      <rect width="100%" height="100%" fill="#1e293b" rx="16"/>
      <circle cx="60" cy="60" r="28" fill="${color}" opacity="0.15"/>
      <path d="M48 60 L56 68 L72 52" stroke="${color}" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
      <text x="110" y="58" font-family="system-ui, -apple-system, sans-serif" font-size="22" font-weight="900" fill="#ffffff" letter-spacing="1">${brandName}</text>
      <text x="110" y="85" font-family="system-ui, -apple-system, sans-serif" font-size="11" font-weight="800" fill="${color}" letter-spacing="4">${label} ASSET</text>
    </svg>`;
    
    const base64Svg = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    handleLogoChange(key, base64Svg);
  };

  const colorsConfig: { key: keyof ColorPalette; label: string; desc: string }[] = [
    { key: 'darkMain', label: 'Dark Main', desc: 'Primary deep brand color used for navigation backgrounds and main headings.' },
    { key: 'lightMain', label: 'Light Main', desc: 'Vibrant highlight color used for active hover items, links, and action buttons.' },
    { key: 'darkSecondary', label: 'Dark Seconary', desc: 'Alternative dark brand accent.' },
    { key: 'lightSecondary', label: 'Light Seconary', desc: 'Subtle light accent or secondary borders.' },
    { key: 'background', label: 'Background', desc: 'Page canvas background color.' }
  ];

  const logosConfig: { key: keyof LogoSet; label: string; desc: string }[] = [
    { key: 'navigation', label: 'Navigation Logo', desc: 'Branded logo displayed in the global top navigation bar.' },
    { key: 'hero', label: 'Hero Logo', desc: 'High-impact major trademark displayed on welcome hubs and home portals.' },
    { key: 'secondary', label: 'Secondary Logo', desc: 'Subtle alternative logo displayed on footer rows and certification tags.' },
    { key: 'sponsor1', label: 'Sponsor 1 Logo', desc: 'Primary regional partner or sponsor logo asset.' },
    { key: 'sponsor2', label: 'Sponsor 2 Logo', desc: 'Secondary regional partner or sponsor logo asset.' }
  ];

  return (
    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-lg p-6 space-y-6">
      {/* Tab Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 pb-5 gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-[#011d3c] tracking-tight uppercase flex items-center space-x-2">
            <Palette className="w-6 h-6 text-[#0094c8]" />
            <span>Identity Workspace & Branding System</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1 uppercase font-extrabold tracking-wider">
            Configure beautiful visual assets, colors, and sponsor layouts for global and regional portals.
          </p>
        </div>

        {/* Global vs Regional Pill Toggles */}
        <div className="bg-slate-100 p-1 rounded-xl flex items-center border border-slate-200">
          <button
            type="button"
            onClick={() => setSelectedSubTab('GLOBAL')}
            className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
              selectedSubTab === 'GLOBAL'
                ? 'bg-white text-[#0094c8] shadow-xs border border-slate-200/40'
                : 'text-slate-600 hover:text-slate-900 bg-transparent border-0'
            }`}
          >
            Universal Global Brand
          </button>
          <button
            type="button"
            onClick={() => setSelectedSubTab('REGIONAL')}
            className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${
              selectedSubTab === 'REGIONAL'
                ? 'bg-white text-[#0094c8] shadow-xs border border-slate-200/40'
                : 'text-slate-600 hover:text-slate-900 bg-transparent border-0'
            }`}
          >
            Regional Brand Overrides
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Side Menu (Only visible under Regional SubTab) */}
        {!isGlobal && (
          <div className="lg:col-span-1 border-r border-slate-100 pr-4 space-y-4">
            <div className="space-y-2">
              <label className="text-[9px] font-black uppercase tracking-wider text-slate-400 block">Search Regional Portals</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filter region..."
                  value={regionSearchQuery}
                  onChange={(e) => setRegionSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-2 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-[#0094c8] bg-slate-50"
                />
              </div>
            </div>

            <div className="space-y-1 max-h-[450px] overflow-y-auto pr-1">
              {filteredRegionOptions.map((tenant) => {
                const isSelected = tenant.id === selectedRegionId;
                return (
                  <button
                    key={tenant.id}
                    type="button"
                    onClick={() => setSelectedRegionId(tenant.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-between ${
                      isSelected
                        ? 'bg-[#0094c8]/10 border-l-4 border-[#0094c8] text-slate-900 font-black'
                        : 'bg-white hover:bg-slate-50 text-slate-700'
                    }`}
                  >
                    <span className="truncate">{tenant.name}</span>
                    <Globe className={`w-3.5 h-3.5 ${isSelected ? 'text-[#0094c8]' : 'text-slate-400'}`} />
                  </button>
                );
              })}
              {filteredRegionOptions.length === 0 && (
                <div className="py-8 text-center text-xs text-slate-400">No regions matches filter.</div>
              )}
            </div>
          </div>
        )}

        {/* Brand Editor Area */}
        <div className={`space-y-6 ${isGlobal ? 'lg:col-span-4' : 'lg:col-span-3'}`}>
          <div className="bg-slate-50 border border-slate-200/60 p-4 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-3">
              <div className="bg-[#0094c8]/10 p-2.5 rounded-xl text-[#0094c8] flex-shrink-0">
                <HelpCircle className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-extrabold text-slate-900">
                  {isGlobal ? 'Universal Organizational Branding' : `Branding Assets for ${activeRegion?.name || 'Selected'} Region`}
                </h4>
                <p className="text-xs text-slate-500">
                  {isGlobal
                    ? 'These color values and logos define the default appearance for the entire unified network system.'
                    : 'Overriding regional identity allows configuring tailored colors and direct sponsor marks for this county.'}
                </p>
              </div>
            </div>
            
            {!isGlobal && (
              <button
                type="button"
                onClick={async () => {
                  const confirmed = await inAppConfirm({
                    title: 'Reset Region Branding',
                    message: 'Are you sure you want to reset this region branding back to global defaults?',
                    confirmText: 'Reset to Defaults',
                    type: 'danger',
                  });
                  if (confirmed) {
                    updateTenantBranding(selectedRegionId, {
                      palette: undefined,
                      logos: undefined
                    } as any);
                    inAppToast({
                      title: 'Branding Reset',
                      message: 'Region branding has been reset to defaults.',
                      type: 'info',
                    });
                  }
                }}
                className="px-3 py-1.5 border border-slate-200 hover:border-red-200 text-slate-600 hover:text-red-600 text-[10px] font-black uppercase rounded-lg transition-colors cursor-pointer bg-white"
              >
                Reset to default
              </button>
            )}
          </div>

          <div className="space-y-8">
            {/* Color Palette Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center space-x-1.5">
                  <Palette className="w-4 h-4 text-[#0094c8]" />
                  <span>Color Theme Swatches</span>
                </h3>
                <span className="text-[9px] font-black text-[#0094c8] bg-[#0094c8]/10 px-2.5 py-0.5 rounded-full uppercase">
                  5 Color Palette Swatches
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {colorsConfig.map((col) => {
                  const currentValue = activePalette[col.key] || '#cccccc';
                  return (
                    <div
                      key={col.key}
                      className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col group relative"
                    >
                      {/* Color Square - clickable to trigger native color picker */}
                      <div 
                        className="relative aspect-square w-full border-b border-slate-150/40 flex items-end p-3 transition-transform" 
                        style={{ backgroundColor: currentValue }}
                      >
                        <input
                          type="color"
                          value={currentValue}
                          onChange={(e) => handleColorChange(col.key, e.target.value)}
                          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
                        />
                        
                        {/* Interactive Pill Overlay */}
                        <div className="bg-white/90 backdrop-blur-xs px-2 py-1 rounded-lg shadow-2xs flex items-center justify-between w-full pointer-events-none group-hover:scale-102 transition-transform">
                          <span className="text-[10px] font-black uppercase tracking-wider text-slate-800 truncate">
                            {col.label}
                          </span>
                        </div>
                      </div>

                      {/* Info & Hex Input */}
                      <div className="p-3 space-y-2 bg-slate-50/50 flex-1 flex flex-col justify-between">
                        <p className="text-[9px] text-slate-400 leading-tight">
                          {col.desc}
                        </p>
                        
                        <div className="flex items-center space-x-1 border-t border-slate-100 pt-2">
                          <span className="text-[10px] font-mono text-slate-400">#</span>
                          <input
                            type="text"
                            value={currentValue.replace('#', '')}
                            onChange={(e) => {
                              const val = e.target.value.trim();
                              if (val.length <= 6) {
                                const cleanedHex = val.startsWith('#') ? val : `#${val}`;
                                handleColorChange(col.key, cleanedHex);
                              }
                            }}
                            className="w-full px-2 py-1 text-xs font-mono border border-slate-200 rounded-lg text-slate-800 focus:ring-1 focus:ring-[#0094c8] bg-white outline-none font-bold"
                            placeholder="FFFFFF"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Logo Assets Section */}
            <div className="space-y-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                <h3 className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center space-x-1.5">
                  <ImageIcon className="w-4 h-4 text-[#0094c8]" />
                  <span>Logo Asset Center</span>
                </h3>
                <span className="text-[9px] font-black text-[#0094c8] bg-[#0094c8]/10 px-2.5 py-0.5 rounded-full uppercase">
                  5 Branded Logo Assets
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {logosConfig.map((log) => {
                  const currentLogo = activeLogos[log.key] || '';
                  return (
                    <div
                      key={log.key}
                      className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between"
                    >
                      {/* Logo Container - aspect-square styled with dark corporate canvas */}
                      <div className="relative aspect-square w-full flex flex-col items-center justify-center p-4 border-b border-slate-100 group">
                        {currentLogo ? (
                          <>
                            <img
                              src={currentLogo}
                              alt={`${log.label} preview`}
                              referrerPolicy="no-referrer"
                              className="max-h-24 max-w-full object-contain transition-transform group-hover:scale-105"
                            />
                            <button
                              type="button"
                              onClick={() => handleLogoChange(log.key, '')}
                              className="absolute right-2 top-2 bg-red-650 hover:bg-red-700 bg-red-600 text-white p-1.5 rounded-lg transition-colors shadow-sm cursor-pointer opacity-90 hover:opacity-100 z-10"
                              title="Remove asset"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <div className="flex flex-col items-center text-center space-y-2">
                            <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-750 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors">
                              <Upload className="w-5 h-5" />
                            </div>
                            <span className="text-[10px] font-black text-slate-400">
                              Upload {log.label}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* File Control Operations */}
                      <div className="p-3 bg-slate-50/50 space-y-3 flex-1 flex flex-col justify-between">
                        <div>
                          <h4 className="text-xs font-extrabold text-slate-800 leading-tight">
                            {log.label}
                          </h4>
                          <p className="text-[9px] text-slate-400 mt-1 leading-normal line-clamp-2 min-h-[24px]">
                            {log.desc}
                          </p>
                        </div>

                        <div className="space-y-2 pt-2 border-t border-slate-100">
                          {/* File Uploader */}
                          <label className="block cursor-pointer">
                            <span className="w-full text-center py-1.5 border border-slate-200 hover:bg-white text-[9px] font-black uppercase rounded-lg transition-colors flex items-center justify-center space-x-1.5 shadow-2xs">
                              <Upload className="w-3 h-3 text-slate-500" />
                              <span>Upload File</span>
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const files = e.target.files;
                                if (files && files.length > 0) {
                                  handleLogoUpload(log.key, files[0]);
                                }
                              }}
                              className="hidden"
                            />
                          </label>

                          {/* Quick brand generator */}
                          <button
                            type="button"
                            onClick={() => handleGeneratePreset(log.key)}
                            className="w-full py-1.5 bg-[#0094c8]/5 bg-[#0094c8]/10 hover:bg-[#0094c8]/20 text-[#0094c8] text-[9px] font-black uppercase rounded-lg transition-all flex items-center justify-center space-x-1.5 cursor-pointer border-0"
                          >
                            <Sparkles className="w-3.5 h-3.5 text-[#0094c8] animate-pulse" />
                            <span>Generate SVG</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Landing Page Background Video Section (Only for Universal Global Brand) */}
            {isGlobal && (
              <div className="space-y-4 pt-6 border-t border-slate-100">
                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center space-x-1.5 font-sans">
                    <PlayCircle className="w-4 h-4 text-[#0094c8]" />
                    <span>Landing Page Hero Background Media</span>
                  </h3>
                  <span className="text-[9px] font-black text-[#0094c8] bg-[#0094c8]/10 px-2.5 py-0.5 rounded-full uppercase font-sans">
                    Universal Background Video
                  </span>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row gap-6">
                  {/* Left Column: Preview or Drag Drop */}
                  <div className="w-full md:w-1/3 flex flex-col items-center justify-center bg-slate-900 rounded-xl relative p-3 overflow-hidden group min-h-[160px] max-h-[220px]">
                    {globalBranding?.heroVideo ? (
                      <>
                        <video
                          src={globalBranding.heroVideo}
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-full h-full object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg z-10">
                          <button
                            type="button"
                            onClick={() => updateGlobalBranding({ heroVideo: '' })}
                            className="bg-red-600 hover:bg-red-700 text-white p-2.5 rounded-full transition-colors shadow-lg cursor-pointer border-0"
                            title="Remove background video"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center text-center space-y-2 text-slate-400">
                        <PlayCircle className="w-10 h-10 text-slate-500 animate-pulse" />
                        <span className="text-[11px] font-bold">No background video configured</span>
                        <span className="text-[9px] text-slate-500 px-2 leading-relaxed">Defaults to static hero background image.</span>
                      </div>
                    )}
                  </div>

                  {/* Right Column: Upload / URL Controls */}
                  <div className="flex-1 flex flex-col justify-between space-y-4">
                    <div>
                      <h4 className="text-xs font-black text-slate-800 uppercase tracking-wide">
                        Video Source Options
                      </h4>
                      <p className="text-[11px] text-slate-500 leading-relaxed mt-1.5">
                        Direct uploads are now saved securely inside your app's **Firebase Storage bucket**, which naturally supports high-definition loops and longer videos. We recommend keeping files under 25MB for optimal network load times.
                      </p>
                    </div>

                    <div className="space-y-3 pt-3 border-t border-slate-200">
                      {/* Upload button wrapper */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <label className={`flex-1 ${uploadProgress !== null ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                          <span className="w-full py-2.5 border border-slate-200 hover:bg-white text-[10px] font-black uppercase rounded-lg transition-colors flex items-center justify-center space-x-1.5 shadow-2xs">
                            <Upload className="w-3.5 h-3.5 text-slate-500" />
                            <span>{uploadProgress !== null ? `Uploading (${uploadProgress}%)` : 'Upload Video to Firebase Storage'}</span>
                          </span>
                          <input
                            type="file"
                            accept="video/mp4,video/webm,video/ogg"
                            disabled={uploadProgress !== null}
                            onChange={(e) => {
                              const files = e.target.files;
                              if (files && files.length > 0) {
                                const file = files[0];
                                
                                // Direct safety guard for ultra large uploads
                                if (file.size > 104857600) { // 100MB
                                  showAlert({
                                    title: "File Size Limit Exceeded",
                                    message: `The selected video file is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). To guarantee lightning-fast portal load times for regional users, background loops are capped at a maximum of 100 MB.`,
                                    type: "warning"
                                  });
                                  e.target.value = '';
                                  return;
                                }

                                setUploadError(null);
                                setUploadProgress(0);

                                try {
                                  // Storage folder layout
                                  const storageRef = ref(storage, `branding/hero-video-${Date.now()}-${file.name}`);
                                  const uploadTask = uploadBytesResumable(storageRef, file);
                                  
                                  uploadTask.on(
                                    'state_changed',
                                    (snapshot) => {
                                      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                                      setUploadProgress(Math.round(progress));
                                    },
                                    (err) => {
                                      console.error("Firebase Storage Upload detailed error: ", err);
                                      setUploadError(
                                        `Could not upload: ${err.message}. Ensure Firebase Storage is initialized in your Firebase console and security rules allow public uploads.`
                                      );
                                      setUploadProgress(null);
                                      e.target.value = '';
                                    },
                                    async () => {
                                      try {
                                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                                        await updateGlobalBranding({ heroVideo: downloadURL });
                                        setUploadProgress(null);
                                      } catch (getUrlErr: any) {
                                        console.error("Error retrieving download URL: ", getUrlErr);
                                        setUploadError(`Failed to fetch media link: ${getUrlErr.message}`);
                                        setUploadProgress(null);
                                      }
                                    }
                                  );
                                } catch (initError: any) {
                                  console.error("Error starting storage task: ", initError);
                                  setUploadError(`Storage Initialization failed: ${initError.message}`);
                                  setUploadProgress(null);
                                  e.target.value = '';
                                }
                              }
                            }}
                            className="hidden"
                          />
                        </label>

                        {globalBranding?.heroVideo && (
                          <button
                            type="button"
                            onClick={() => updateGlobalBranding({ heroVideo: '' })}
                            className="py-2.5 px-4 border border-red-200 hover:bg-red-50 text-red-655 hover:text-red-700 text-[10px] font-black uppercase rounded-lg transition-colors flex items-center justify-center space-x-1 bg-white"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Remove Video</span>
                          </button>
                        )}
                      </div>

                      {/* Progress bar */}
                      {uploadProgress !== null && (
                        <div className="space-y-1.5">
                          <div className="flex justify-between text-[10px] font-black text-[#0094c8] uppercase font-sans">
                            <span>Uploading background loop segment...</span>
                            <span>{uploadProgress}%</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden shadow-inner">
                            <div 
                              className="bg-[#0094c8] h-full rounded-full transition-all duration-300 shadow-md animate-pulse" 
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Upload Error Box */}
                      {uploadError && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-1">
                          <p className="text-[10px] font-black text-red-700 uppercase tracking-wider flex items-center space-x-1">
                            <Info className="w-3.5 h-3.5 shrink-0" />
                            <span>Storage Upload Interrupted</span>
                          </p>
                          <p className="text-[10px] font-bold text-red-650 leading-relaxed">
                            {uploadError}
                          </p>
                        </div>
                      )}

                      {/* Direct URL Input */}
                      <div className="space-y-1 pt-1">
                        <label className="text-[10px] pb-1 font-black text-slate-500 uppercase tracking-wider block">
                          Or Externally Hosted/Direct Video Link:
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="url"
                            placeholder="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
                            value={globalBranding?.heroVideo && !globalBranding.heroVideo.startsWith('data:') && !globalBranding.heroVideo.startsWith('blob:') ? globalBranding.heroVideo : ''}
                            onChange={(e) => updateGlobalBranding({ heroVideo: e.target.value })}
                            className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-lg text-slate-800 focus:ring-1 focus:ring-[#0094c8] bg-white outline-none font-bold"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Global Default Base URL Configuration (When Global Brand Selected) */}
            {isGlobal && (
              <div className="space-y-4 pt-6 border-t border-slate-100 animate-fadeIn">
                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center space-x-1.5 font-sans">
                    <Globe className="w-4 h-4 text-[#0094c8]" />
                    <span>Global Default Portal Domain & Base URL</span>
                  </h3>
                  <span className="text-[9px] font-black text-[#0094c8] bg-[#0094c8]/10 px-2.5 py-0.5 rounded-full uppercase font-sans">
                    Master Portal Routing
                  </span>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row gap-6">
                  <div className="flex-1 flex flex-col justify-between space-y-4">
                    <div>
                      <h4 className="text-xs font-black text-slate-800 uppercase tracking-wide">
                        Global Portal Hyperlinks & Default Email Domain
                      </h4>
                      <p className="text-[11px] text-slate-500 leading-relaxed mt-1.5 font-sans">
                        Designate the default domain (e.g. <strong className="text-slate-700">wblportal.com</strong>) used across all standard outbound emails, password resets, welcome messages, and student/partner links for all non-customized regions. Regional portals configured with their own domain (such as Spartanburg &rarr; <strong className="text-slate-700">thespartanburgedge.com</strong>) will automatically route to their dedicated domain.
                      </p>
                    </div>

                    <div className="space-y-3 pt-3 border-t border-slate-200">
                      <div className="space-y-1.5">
                        <label className="text-[10px] pb-1 font-black text-slate-500 uppercase tracking-wider block">
                          Master Portal Domain / Default Base URL:
                        </label>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="relative flex-1">
                            <Globe className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                            <input
                              type="text"
                              placeholder="e.g. wblportal.com"
                              value={globalBranding?.customDomain || ''}
                              onChange={(e) => {
                                updateGlobalBranding({
                                  customDomain: e.target.value.trim()
                                });
                              }}
                              className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-lg text-slate-800 focus:ring-1 focus:ring-[#0094c8] bg-white outline-none font-bold"
                            />
                          </div>
                          {globalBranding?.customDomain && (
                            <button
                              type="button"
                              onClick={() => {
                                updateGlobalBranding({
                                  customDomain: 'wblportal.com'
                                });
                              }}
                              className="py-2.5 px-4 border border-slate-200 hover:bg-slate-100 text-slate-650 text-[10px] font-black uppercase rounded-lg transition-colors flex items-center justify-center space-x-1 bg-white cursor-pointer"
                            >
                              <RefreshCw className="w-3.5 h-3.5 text-[#0094c8]" />
                              <span>Reset (wblportal.com)</span>
                            </button>
                          )}
                        </div>
                        <p className="text-[10px] text-[#0094c8] font-bold font-mono mt-1">
                          Active Global Base URL: {globalBranding?.customDomain ? (globalBranding.customDomain.startsWith('http') ? globalBranding.customDomain : `https://${globalBranding.customDomain}`) : 'https://wblportal.com'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Custom Domain Configuration (Only for Regional Brands) */}
            {!isGlobal && (
              <div className="space-y-4 pt-6 border-t border-slate-100 animate-fadeIn">
                <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center space-x-1.5 font-sans">
                    <Globe className="w-4 h-4 text-[#0094c8]" />
                    <span>Custom Region Domain Portal</span>
                  </h3>
                  <span className="text-[9px] font-black text-[#0094c8] bg-[#0094c8]/10 px-2.5 py-0.5 rounded-full uppercase font-sans">
                    Region Portal Routing
                  </span>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row gap-6">
                  <div className="flex-1 flex flex-col justify-between space-y-4">
                    <div>
                      <h4 className="text-xs font-black text-slate-800 uppercase tracking-wide">
                        Region-Specific Hyperlinks & Domain settings
                      </h4>
                      <p className="text-[11px] text-slate-500 leading-relaxed mt-1.5 font-sans">
                        Provide a custom domain (e.g. <strong className="text-slate-700">thespartanburgedge.com</strong>) for the <strong>{activeRegion?.name}</strong> portal. When configured, all automated emails, hyperlinks, and logo navigation tags for this region will direct users to this custom domain instead of the default tenant subdomain.
                      </p>
                    </div>

                    <div className="space-y-3 pt-3 border-t border-slate-200">
                      <div className="space-y-1.5">
                        <label className="text-[10px] pb-1 font-black text-slate-500 uppercase tracking-wider block">
                          Custom Portal Domain / URL Address:
                        </label>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <div className="relative flex-1">
                            <Globe className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                            <input
                              type="text"
                              placeholder="e.g. thespartanburgedge.com"
                              value={activeRegion?.customDomain || ''}
                              onChange={(e) => {
                                updateTenantBranding(selectedRegionId, {
                                  customDomain: e.target.value.trim()
                                });
                              }}
                              className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-lg text-slate-800 focus:ring-1 focus:ring-[#0094c8] bg-white outline-none font-bold"
                            />
                          </div>
                          {activeRegion?.customDomain && (
                            <button
                              type="button"
                              onClick={() => {
                                updateTenantBranding(selectedRegionId, {
                                  customDomain: ''
                                });
                              }}
                              className="py-2.5 px-4 border border-red-200 hover:bg-red-50 text-red-650 hover:text-red-750 text-[10px] font-black uppercase rounded-lg transition-colors flex items-center justify-center space-x-1 bg-white cursor-pointer"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Clear Domain</span>
                            </button>
                          )}
                        </div>
                        {activeRegion?.customDomain && (
                          <p className="text-[10px] text-[#0094c8] font-bold font-mono mt-1">
                            Active Base URL: {activeRegion.customDomain.startsWith('http') ? activeRegion.customDomain : `https://${activeRegion.customDomain}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

// ==========================================
// IMAGERY & MEDIA WORKSPACE COMPONENT
// ==========================================
const ImageryTabContent: React.FC = () => {
  const { 
    globalBranding, 
    updateGlobalBranding 
  } = useWBL();

  const [selectedSubTab, setSelectedSubTab] = useState<'BANNERS' | 'SUBCLUSTERS'>('BANNERS');

  // Upload State for Banner Images
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Subcluster Accordion & Upload states
  const [expandedCluster, setExpandedCluster] = useState<string | null>(null);
  const [subclusterUpload, setSubclusterUpload] = useState<{
    subcluster: string;
    spotIndex: number;
    progress: number | null;
    error: string | null;
  } | null>(null);

  const handleSubclusterImageUpload = (subclusterName: string, spotIndex: number, file: File) => {
    setSubclusterUpload({
      subcluster: subclusterName,
      spotIndex,
      progress: 0,
      error: null
    });

    const fileName = `subclusters/subcluster-${Date.now()}-${spotIndex}-${file.name}`;
    const storageRef = ref(storage, fileName);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setSubclusterUpload(prev => prev ? { ...prev, progress: Math.round(progress) } : null);
      },
      (err) => {
        setSubclusterUpload(prev => prev ? { ...prev, error: `Upload failed: ${err.message}`, progress: null } : null);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const currentMap = globalBranding?.subclusterImages || {};
          const currentArray = [...(currentMap[subclusterName] || [])];
          
          while (currentArray.length < 3) {
            currentArray.push('');
          }
          currentArray[spotIndex] = downloadURL;

          const updatedMap = {
            ...currentMap,
            [subclusterName]: currentArray
          };

          await updateGlobalBranding({ subclusterImages: updatedMap });
          setSubclusterUpload(null);
        } catch (e: any) {
          setSubclusterUpload(prev => prev ? { ...prev, error: `Failed to fetch link: ${e.message}`, progress: null } : null);
        }
      }
    );
  };

  const handleSubclusterImageDelete = async (subclusterName: string, spotIndex: number) => {
    const currentMap = globalBranding?.subclusterImages || {};
    const currentArray = [...(currentMap[subclusterName] || [])];
    if (currentArray[spotIndex]) {
      currentArray[spotIndex] = ''; // clear out
      
      const updatedMap = {
        ...currentMap,
        [subclusterName]: currentArray
      };
      await updateGlobalBranding({ subclusterImages: updatedMap });
    }
  };

  const handleClusterImageUpload = (clusterName: string, spotIndex: number, file: File) => {
    setSubclusterUpload({
      subcluster: `__cluster_${clusterName}`,
      spotIndex,
      progress: 0,
      error: null
    });

    const fileName = `clusters/cluster-${Date.now()}-${spotIndex}-${file.name}`;
    const storageRef = ref(storage, fileName);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setSubclusterUpload(prev => prev ? { ...prev, progress: Math.round(progress) } : null);
      },
      (err) => {
        setSubclusterUpload(prev => prev ? { ...prev, error: `Upload failed: ${err.message}`, progress: null } : null);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const currentMap = globalBranding?.clusterImages || {};
          const currentArray = [...(currentMap[clusterName] || [])];
          
          while (currentArray.length < 3) {
            currentArray.push('');
          }
          currentArray[spotIndex] = downloadURL;

          const updatedMap = {
            ...currentMap,
            [clusterName]: currentArray
          };

          await updateGlobalBranding({ clusterImages: updatedMap });
          setSubclusterUpload(null);
        } catch (e: any) {
          setSubclusterUpload(prev => prev ? { ...prev, error: `Failed to fetch link: ${e.message}`, progress: null } : null);
        }
      }
    );
  };

  const handleClusterImageDelete = async (clusterName: string, spotIndex: number) => {
    const currentMap = globalBranding?.clusterImages || {};
    const currentArray = [...(currentMap[clusterName] || [])];
    if (currentArray[spotIndex]) {
      currentArray[spotIndex] = ''; // clear out
      
      const updatedMap = {
        ...currentMap,
        [clusterName]: currentArray
      };
      await updateGlobalBranding({ clusterImages: updatedMap });
    }
  };

  const activeHeroImage = globalBranding?.heroImage;
  const activePageBanners = globalBranding?.pageBanners || {};

  const handleImageUpload = (file: File) => {
    setUploadError(null);
    setUploadProgress(0);

    const fileName = `imagery/banner-global-${Date.now()}-${file.name}`;
    const storageRef = ref(storage, fileName);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(Math.round(progress));
      },
      (err) => {
        setUploadError(`Upload failed: ${err.message}`);
        setUploadProgress(null);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          await updateGlobalBranding({ heroImage: downloadURL });
          setUploadProgress(null);
        } catch (e: any) {
          setUploadError(`Failed to fetch link: ${e.message}`);
          setUploadProgress(null);
        }
      }
    );
  };

  const handlePageImageUpload = (pageKey: keyof PageBanners, file: File) => {
    setUploadError(null);
    setUploadProgress(0);

    const fileName = `imagery/banner-global-${String(pageKey)}-${Date.now()}-${file.name}`;
    const storageRef = ref(storage, fileName);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(Math.round(progress));
      },
      (err) => {
        setUploadError(`Upload failed: ${err.message}`);
        setUploadProgress(null);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const currentBanners = globalBranding?.pageBanners || {};
          const updatedBanners = { ...currentBanners, [pageKey]: downloadURL };
          await updateGlobalBranding({ pageBanners: updatedBanners });
          setUploadProgress(null);
        } catch (e: any) {
          setUploadError(`Failed to fetch link: ${e.message}`);
          setUploadProgress(null);
        }
      }
    );
  };

  const PAGE_KEYS: { key: keyof PageBanners; label: string }[] = [
    { key: 'dashboard', label: 'Primary Dashboards' },
    { key: 'opportunities', label: 'Opportunities Board' },
    { key: 'applications', label: 'Student Applications' },
    { key: 'placements', label: 'Placement Registers' },
    { key: 'profile', label: 'Profile' },
    { key: 'organizations', label: 'Organization Discovery' },
    { key: 'analytics', label: 'Reports' },
    { key: 'registry', label: 'User Registry Console' },
    { key: 'console', label: 'Admin SaaS Console' },
    { key: 'advisees', label: 'Advisees Directory' },
    { key: 'interviews', label: 'Interviews' },
    { key: 'advisors', label: 'Advisors' },
    { key: 'foundations', label: 'Foundations' },
    { key: 'messages', label: 'Messages' },
    { key: 'settings', label: 'Settings' }
  ];

  return (
    <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-lg p-6 space-y-6">
      {/* Tab Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 pb-5 gap-4">
        <div>
          <h2 className="text-xl md:text-2xl font-black text-[#011d3c] tracking-tight uppercase flex items-center space-x-2">
            <ImageIcon className="w-6 h-6 text-[#0094c8]" />
            <span>Imagery & Media Assets</span>
          </h2>
          <p className="text-xs text-slate-400 mt-1 uppercase font-extrabold tracking-wider">
            Manage high-definition banners and pathway visual media.
          </p>
        </div>

        {/* Banners vs Subclusters Pill Toggles */}
        <div className="bg-slate-100 p-1 rounded-xl flex items-center border border-slate-200 shadow-3xs">
          <button
            type="button"
            onClick={() => setSelectedSubTab('BANNERS')}
            className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer border-0 ${
              selectedSubTab === 'BANNERS'
                ? 'bg-white text-[#0094c8] shadow-xs border border-slate-200/40'
                : 'text-slate-600 hover:text-slate-900 bg-transparent'
            }`}
          >
            Universal Page Banners
          </button>
          <button
            type="button"
            onClick={() => setSelectedSubTab('SUBCLUSTERS')}
            className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer border-0 ${
              selectedSubTab === 'SUBCLUSTERS'
                ? 'bg-white text-[#0094c8] shadow-xs border border-slate-200/40'
                : 'text-slate-600 hover:text-slate-900 bg-transparent'
            }`}
          >
            Subcluster Opportunity Imagery
          </button>
        </div>
      </div>

      <div className="w-full">
        {selectedSubTab === 'BANNERS' ? (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* Universal Fallback Banner */}
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                 <h3 className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center space-x-1.5">
                  <ImageIcon className="w-4 h-4 text-[#0094c8]" />
                  <span>Primary Global Fallback Banner Image</span>
                </h3>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 flex flex-col md:flex-row gap-6">
                {/* Preview Column */}
                <div className="w-full md:w-1/2 flex flex-col items-center justify-center bg-slate-900 rounded-xl relative p-3 overflow-hidden group min-h-[180px]">
                  {activeHeroImage ? (
                    <>
                      <img 
                        src={activeHeroImage}
                        alt="Banner Preview"
                        className="w-full h-full object-cover rounded-lg"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg z-10">
                        <button
                          type="button"
                          onClick={() => {
                            updateGlobalBranding({ heroImage: '' });
                          }}
                          className="bg-red-600 hover:bg-red-700 text-white p-2.5 rounded-full transition-colors shadow-lg cursor-pointer border-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center text-center space-y-2 text-slate-400">
                      <ImageIcon className="w-10 h-10 text-slate-500 opacity-20" />
                      <span className="text-[11px] font-bold">No custom fallback configured</span>
                      <span className="text-[9px] text-slate-500 px-2 leading-relaxed">System will use local static assets or hero video.</span>
                    </div>
                  )}
                </div>

                {/* Upload Column */}
                <div className="flex-1 flex flex-col justify-between space-y-4">
                  <div>
                    <h4 className="text-sm font-extrabold text-slate-900">
                      Global Fallback Banner
                    </h4>
                    <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                      Used when no page-specific banner is assigned. 
                      Resolution: 1920x400 recommended.
                    </p>
                  </div>

                  <div className="space-y-3 pt-3 border-t border-slate-100">
                    <label className={`block ${uploadProgress !== null ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                      <span className="w-full py-2 border border-slate-200 hover:bg-white text-[10px] font-black uppercase rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-2xs">
                        <Upload className="w-3.5 h-3.5 text-slate-500" />
                        <span>{uploadProgress !== null ? `Uploading (${uploadProgress}%)` : 'Upload Fallback'}</span>
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadProgress !== null}
                        onChange={(e) => {
                          if (e.target.files?.[0]) handleImageUpload(e.target.files[0]);
                        }}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Page Specific Banners Grid */}
            <div className="space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                 <h3 className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center space-x-1.5">
                  <Sparkles className="w-4 h-4 text-[#0094c8]" />
                  <span>Page-Specific Branded Banners</span>
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {PAGE_KEYS.map((page) => {
                  const bannerUrl = activePageBanners[page.key];
                  return (
                    <div key={page.key} className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm flex flex-col group h-full">
                      {/* Tiny Preview Box */}
                      <div className="h-24 relative overflow-hidden flex items-center justify-center transition-colors">
                        {bannerUrl ? (
                          <>
                            <img 
                              src={bannerUrl} 
                              alt={page.label} 
                              className="w-full h-full object-cover opacity-80"
                              referrerPolicy="no-referrer"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                               <button
                                type="button"
                                onClick={() => {
                                  const updated = { ...activePageBanners };
                                  delete updated[page.key];
                                  updateGlobalBranding({ pageBanners: updated });
                                }}
                                className="bg-red-600 text-white p-1.5 rounded-lg shadow-lg hover:bg-red-700 transition-colors cursor-pointer border-0"
                              >
                                 <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest opacity-30">
                            {page.label}
                          </div>
                        )}
                      </div>

                      <div className="p-3 flex flex-col justify-between flex-1">
                        <div>
                          <h4 className="text-xs font-black text-slate-900 uppercase tracking-tight">{page.label}</h4>
                          <p className="text-[9px] text-slate-400 mt-0.5">Banner for {page.key} view.</p>
                        </div>

                        <div className="mt-3">
                          <label className={`block ${uploadProgress !== null ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                            <span className="w-full text-center py-1.5 border border-slate-100 group-hover:border-slate-200 bg-slate-50 group-hover:bg-white transition-all text-[9px] font-black uppercase rounded-lg flex items-center justify-center space-x-1.5">
                              <Upload className="w-3 h-3 text-slate-400" />
                              <span>{uploadProgress !== null ? '...' : 'Upload'}</span>
                            </span>
                            <input 
                              type="file" 
                              accept="image/*"
                              disabled={uploadProgress !== null}
                              className="hidden" 
                              onChange={(e) => {
                                if (e.target.files?.[0]) handlePageImageUpload(page.key, e.target.files[0]);
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4 animate-in fade-in duration-300">
            {/* Subcluster-Specific Imagery Assets */}
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
               <h3 className="text-xs font-black uppercase tracking-wider text-slate-600 flex items-center space-x-1.5">
                <Sparkles className="w-4 h-4 text-[#0094c8]" />
                <span>Subcluster-Specific Opportunity Imagery</span>
               </h3>
               <span className="text-[10px] font-black uppercase tracking-wider text-[#0094c8] bg-[#0094c8]/10 px-2.5 py-1 rounded-md">
                Configure 3 Upload Spots Per Subcluster
               </span>
            </div>

            <p className="text-[11px] text-slate-500 max-w-2xl leading-relaxed">
              Upload specialized, high-quality images for each subcluster. When subclusters are assigned during opportunity creation, opportunity cards will automatically pull randomized selections among the uploaded spots. If a spot is unconfigured, the cards fallback gracefully to general pathway illustrations.
            </p>

            <div className="space-y-3">
              {Object.keys(SUBCLUSTERS_MAP).map((clusterName) => {
                const subclusters = SUBCLUSTERS_MAP[clusterName];
                const isExpanded = expandedCluster === clusterName;

                // Count completion stats
                let configuredSpots = 0;
                let totalSpots = subclusters.length * 3;
                subclusters.forEach(sub => {
                  const arr = globalBranding?.subclusterImages?.[sub] || [];
                  arr.forEach(url => {
                    if (url) configuredSpots++;
                  });
                });

                return (
                  <div key={clusterName} className="border border-slate-200 rounded-2xl bg-white overflow-hidden shadow-2xs transition-all">
                    {/* Accordion Trigger Header */}
                    <button
                      type="button"
                      onClick={() => setExpandedCluster(isExpanded ? null : clusterName)}
                      className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left font-sans cursor-pointer border-0"
                    >
                      <div className="space-y-1">
                        <span className="text-xs font-black text-slate-900 uppercase tracking-tight block">
                          {clusterName}
                        </span>
                        <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider flex items-center space-x-2">
                          <span>{subclusters.length} Subclusters</span>
                          <span className="text-slate-350">•</span>
                          <span className={configuredSpots > 0 ? (configuredSpots === totalSpots ? 'text-emerald-600 font-black' : 'text-[#0094c8] font-black') : 'text-slate-400'}>
                            {configuredSpots} of {totalSpots} Upload Spots Configured
                          </span>
                        </span>
                      </div>

                      <div className="p-1 rounded-lg bg-slate-100 border border-slate-200">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-slate-600" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-600" />
                        )}
                      </div>
                    </button>

                    {/* Accordion Content Panel */}
                    {isExpanded && (
                      <div className="border-t border-slate-150 p-5 bg-slate-50/40 space-y-6">
                        {/* Cluster Fallback */}
                        <div className="space-y-3 bg-white p-4 rounded-xl border border-slate-150 shadow-3xs relative overflow-hidden mb-6">
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#0094c8]"></div>
                          <div className="pl-2">
                            <h4 className="text-xs font-black text-[#011d3c] uppercase tracking-wide">
                              {clusterName} (Cluster Fallback)
                            </h4>
                            <p className="text-[10px] text-slate-500 mt-0.5 mb-3">
                              Images uploaded here will be used if a specific subcluster doesn't have an image configured.
                            </p>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {[0, 1, 2].map((spotIdx) => {
                              const clusterSpots = globalBranding?.clusterImages?.[clusterName] || ['', '', ''];
                              const spotUrl = clusterSpots[spotIdx];
                              const isUploading = subclusterUpload?.subcluster === `__cluster_${clusterName}` && subclusterUpload?.spotIndex === spotIdx;

                              return (
                                <div key={`cluster-${spotIdx}`} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-4xs group h-32 flex flex-col relative">
                                  {spotUrl ? (
                                    <>
                                      <img 
                                        src={spotUrl} 
                                        alt={`Cluster Spot ${spotIdx + 1}`} 
                                        className="w-full h-full object-cover" 
                                        referrerPolicy="no-referrer"
                                      />
                                      {/* Hover Overlay */}
                                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2.5 z-10 text-white">
                                        <div className="flex justify-between items-center">
                                          <span className="text-[9px] font-black uppercase tracking-wider bg-black/40 px-2 py-0.5 rounded-sm">
                                            Spot {spotIdx + 1}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={() => handleClusterImageDelete(clusterName, spotIdx)}
                                            className="bg-red-600 hover:bg-red-700 text-white p-1 rounded-md transition-colors border-0 cursor-pointer shadow-md"
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </button>
                                        </div>
                                        <p className="text-[8px] font-semibold text-slate-200 truncate">
                                          {spotUrl}
                                        </p>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="flex-1 flex flex-col items-center justify-center p-4 text-center relative border border-dashed border-slate-300 rounded-xl">
                                      {isUploading ? (
                                        <div className="space-y-2">
                                          <div className="relative w-8 h-8 flex items-center justify-center">
                                            <RefreshCw className="w-5 h-5 text-[#0094c8] animate-spin" strokeWidth={3} />
                                          </div>
                                          <span className="text-[9px] font-black text-[#0094c8] uppercase tracking-wider block">
                                            {subclusterUpload.progress}%
                                          </span>
                                        </div>
                                      ) : (
                                        <>
                                          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-100 px-2 py-0.5 rounded-sm mb-1">
                                            Spot {spotIdx + 1}
                                          </span>
                                          <p className="text-[9px] text-slate-400 font-semibold mb-2">No fallback configured</p>
                                          <label className="cursor-pointer">
                                            <span className="px-2.5 py-1 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-[9px] font-black uppercase tracking-wider text-slate-700 transition-colors shadow-2xs flex items-center space-x-1.5 cursor-pointer">
                                              <Upload className="w-2.5 h-2.5 text-slate-450" />
                                              <span>Select Image</span>
                                            </span>
                                            <input 
                                              type="file" 
                                              accept="image/*"
                                              className="hidden" 
                                              onChange={(e) => {
                                                if (e.target.files?.[0]) {
                                                  handleClusterImageUpload(clusterName, spotIdx, e.target.files[0]);
                                                }
                                              }}
                                            />
                                          </label>
                                        </>
                                      )}
                                    </div>
                                  )}

                                  {/* Spot Error banner */}
                                  {isUploading && subclusterUpload.error && (
                                    <div className="absolute inset-0 bg-red-50/95 flex flex-col items-center justify-center p-2 text-center animate-pulse z-20">
                                      <p className="text-[8px] font-bold text-red-700 leading-tight">
                                        {subclusterUpload.error}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {subclusters.map((subclusterName) => {
                          const currentSpots = globalBranding?.subclusterImages?.[subclusterName] || ['', '', ''];
                          
                          return (
                            <div key={subclusterName} className="space-y-3 bg-white p-4 rounded-xl border border-slate-150 shadow-3xs">
                              <h4 className="text-xs font-black text-[#011d3c] uppercase tracking-wide">
                                {subclusterName}
                              </h4>

                              {/* 3 Upload Spots Grid */}
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {[0, 1, 2].map((spotIdx) => {
                                  const spotUrl = currentSpots[spotIdx];
                                  const isUploading = subclusterUpload?.subcluster === subclusterName && subclusterUpload?.spotIndex === spotIdx;

                                  return (
                                    <div key={spotIdx} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-4xs group h-32 flex flex-col relative">
                                      {spotUrl ? (
                                        <>
                                          <img 
                                            src={spotUrl} 
                                            alt={`Spot ${spotIdx + 1}`} 
                                            className="w-full h-full object-cover" 
                                            referrerPolicy="no-referrer"
                                          />
                                          {/* Hover Overlay */}
                                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2.5 z-10 text-white">
                                            <div className="flex justify-between items-center">
                                              <span className="text-[9px] font-black uppercase tracking-wider bg-black/40 px-2 py-0.5 rounded-sm">
                                                Spot {spotIdx + 1}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() => handleSubclusterImageDelete(subclusterName, spotIdx)}
                                                className="bg-red-600 hover:bg-red-700 text-white p-1 rounded-md transition-colors border-0 cursor-pointer shadow-md"
                                              >
                                                <Trash2 className="w-3.5 h-3.5" />
                                              </button>
                                            </div>
                                            <p className="text-[8px] font-semibold text-slate-200 truncate">
                                              {spotUrl}
                                            </p>
                                          </div>
                                        </>
                                      ) : (
                                        <div className="flex-1 flex flex-col items-center justify-center p-4 text-center relative border border-dashed border-slate-300 rounded-xl">
                                          {isUploading ? (
                                            <div className="space-y-2">
                                              <div className="relative w-8 h-8 flex items-center justify-center">
                                                <RefreshCw className="w-5 h-5 text-[#0094c8] animate-spin" strokeWidth={3} />
                                              </div>
                                              <span className="text-[9px] font-black text-[#0094c8] uppercase tracking-wider block">
                                                {subclusterUpload.progress}%
                                              </span>
                                            </div>
                                          ) : (
                                            <>
                                              <span className="text-[9px] font-black uppercase tracking-widest text-slate-400 bg-slate-100 px-2 py-0.5 rounded-sm mb-1">
                                                Spot {spotIdx + 1}
                                              </span>
                                              <p className="text-[9px] text-slate-400 font-semibold mb-2">No image configured</p>
                                              <label className="cursor-pointer">
                                                <span className="px-2.5 py-1 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-[9px] font-black uppercase tracking-wider text-slate-700 transition-colors shadow-2xs flex items-center space-x-1.5 cursor-pointer">
                                                  <Upload className="w-2.5 h-2.5 text-slate-450" />
                                                  <span>Select Image</span>
                                                </span>
                                                <input 
                                                  type="file" 
                                                  accept="image/*"
                                                  className="hidden" 
                                                  onChange={(e) => {
                                                    if (e.target.files?.[0]) {
                                                      handleSubclusterImageUpload(subclusterName, spotIdx, e.target.files[0]);
                                                    }
                                                  }}
                                                />
                                              </label>
                                            </>
                                          )}
                                        </div>
                                      )}

                                      {/* Spot Error banner */}
                                      {isUploading && subclusterUpload.error && (
                                        <div className="absolute inset-0 bg-red-50/95 flex flex-col items-center justify-center p-2 text-center animate-pulse z-20">
                                          <p className="text-[8px] font-bold text-red-700 leading-tight">
                                            {subclusterUpload.error}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Progress & Error Feedback Sticky Bar */}
        {(uploadProgress !== null || uploadError) && (
          <div className="fixed bottom-6 right-6 z-[100] max-w-xs w-full bg-white border border-slate-200 rounded-2xl shadow-2xl p-4 animate-in slide-in-from-bottom-4">
             {uploadProgress !== null && (
               <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-[#0094c8]/80 uppercase tracking-wider animate-pulse">Syncing Visual Asset...</span>
                    <span className="text-[10px] font-black text-slate-400">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                     <div 
                      className="bg-[#0094c8] h-full rounded-full transition-all duration-300" 
                      style={{ width: `${uploadProgress}%` }}
                     />
                  </div>
               </div>
             )}
             {uploadError && (
               <div className="bg-red-50 p-2 rounded-lg border border-red-100 flex items-start space-x-2">
                  <Info className="w-3.5 h-3.5 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-[9px] font-bold text-red-700 leading-tight">{uploadError}</p>
               </div>
             )}
          </div>
        )}
      </div>
    </div>
  );
};

// Custom helper: Regional Districts Mapper
const getDistrictsForTenant = (tenantId: string): string[] => {
  return [];
};

// Custom helper: Regional Schools Mapper
const getSchoolsForTenant = (tenantId: string): string[] => {
  return [];
};

const RegistryTabContent: React.FC = () => {
  return null;
};

const DeprecatedRegistryTabContent: React.FC = () => {
  const { 
    users, 
    tenants, 
    updateUserProfile, 
    deleteUserProfile, 
    addUserProfile,
    createAuthenticatedUser,
    allSchools,
    allDistricts
  } = useWBL();

  const [selectedRegionFilter, setSelectedRegionFilter] = useState('ALL');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('ALL');
  const [searchUserQuery, setSearchUserQuery] = useState('');
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [expandedUserUid, setExpandedUserUid] = useState<string | null>(null);
  const [selectedSchoolForAdvisor, setSelectedSchoolForAdvisor] = useState<Record<string, string>>({});
  
  // Real-time flash saving UI feedback
  const [saveStatus, setSaveStatus] = useState<Record<string, 'saved' | 'saving' | null>>({});

  // Account provisioning fields
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('student');
  const [newUserTenantId, setNewUserTenantId] = useState('training');
  const [newUserDistrict, setNewUserDistrict] = useState('');
  const [newUserSchool, setNewUserSchool] = useState('');

  const getDistrictsForTenantDynamic = (tenantId: string) => {
    if (!allDistricts) return [];
    const matched = allDistricts.filter(d => d && d.regionId === tenantId);
    matched.sort((a,b) => (a.name || '').localeCompare(b.name || ''));
    return matched;
  };

  const getSchoolsForTenantDynamic = (tenantId: string) => {
    if (!allSchools) return [];
    const matchedSchools = allSchools.filter(s => s && s.regionId === tenantId);
    matchedSchools.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return matchedSchools;
  };

  const handleUpdateUser = async (uid: string, updates: any) => {
    setSaveStatus(prev => ({ ...prev, [uid]: 'saving' }));
    try {
      await updateUserProfile(uid, updates);
      setSaveStatus(prev => ({ ...prev, [uid]: 'saved' }));
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [uid]: null }));
      }, 1500);
    } catch {
      setSaveStatus(prev => ({ ...prev, [uid]: null }));
    }
  };

  const toggleUserSchoolAssociation = async (user: UserProfile, targetSchoolId: string) => {
    const currentSchools = user.schoolsServedIds || [];
    const primarySchoolId = user.schoolId;
    
    if (primarySchoolId === targetSchoolId) {
      // Toggling OFF the primary school
      if (currentSchools.length > 0) {
        const nextPrimaryId = currentSchools[0];
        const nextSchoolsServedList = currentSchools.filter(id => id !== nextPrimaryId);
        const newPrimarySchool = (allSchools || []).find(s => s && s.id === nextPrimaryId);
        
        const nextSchoolsServedNames = nextSchoolsServedList.map(id => {
          const found = (allSchools || []).find(s => s && s.id === id);
          return found ? found.name : id;
        });

        await handleUpdateUser(user.uid, {
          schoolId: nextPrimaryId,
          school: newPrimarySchool?.name || '',
          schoolsServedIds: nextSchoolsServedList,
          schoolsServed: nextSchoolsServedNames
        });
      } else {
        // Unassign completely
        await handleUpdateUser(user.uid, {
          schoolId: '',
          school: '',
          schoolsServedIds: [],
          schoolsServed: []
        });
      }
    } else if (currentSchools.includes(targetSchoolId)) {
      // Toggling OFF an additional school
      const nextSchoolsServedList = currentSchools.filter(id => id !== targetSchoolId);
      const nextSchoolsServedNames = nextSchoolsServedList.map(id => {
        const found = (allSchools || []).find(s => s && s.id === id);
        return found ? found.name : id;
      });
      await handleUpdateUser(user.uid, { 
        schoolsServedIds: nextSchoolsServedList,
        schoolsServed: nextSchoolsServedNames
      });
    } else {
      // Toggling ON a school
      if (!primarySchoolId) {
        const newPrimarySchool = (allSchools || []).find(s => s && s.id === targetSchoolId);
        await handleUpdateUser(user.uid, {
          schoolId: targetSchoolId,
          school: newPrimarySchool?.name || ''
        });
      } else {
        const nextSchoolsServedList = [...currentSchools, targetSchoolId];
        const nextSchoolsServedNames = nextSchoolsServedList.map(id => {
          const found = (allSchools || []).find(s => s && s.id === id);
          return found ? found.name : id;
        });
        await handleUpdateUser(user.uid, { 
          schoolsServedIds: nextSchoolsServedList,
          schoolsServed: nextSchoolsServedNames
        });
      }
    }
  };

  const handleDeleteUser = async (uid: string, name: string) => {
    const confirmed = await inAppConfirm({
      title: "Delete Profile",
      message: `Are you sure you want to delete profile for ${name}? This action cannot be undone.`,
      confirmText: "Delete Profile",
      type: "danger",
    });
    if (confirmed) {
      await deleteUserProfile(uid);
      inAppToast({
        title: "Profile Deleted",
        message: `User profile for ${name} was deleted.`,
        type: "info",
      });
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserName.trim() || !newUserEmail.trim()) {
      inAppAlert({
        title: "Missing Information",
        message: "Name and Email are required.",
        type: "warning",
      });
      return;
    }

    const matchedTenant = (tenants || []).find(t => t && t.id === newUserTenantId);
    const names = (newUserName || '').trim().split(' ').filter(Boolean);
    const initials = names.map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
    const matchedDistrict = (allDistricts || []).find(d => d && d.name === newUserDistrict && d.regionId === newUserTenantId);
    const matchedSchool = (allSchools || []).find(s => s && s.id === newUserSchool);

    const profile: UserProfile = {
      uid: '', // Placeholder, will be replaced by Auth UID
      name: newUserName.trim(),
      email: newUserEmail.trim().toLowerCase(),
      role: newUserRole,
      tenantId: newUserTenantId,
      region: matchedTenant ? matchedTenant.name : 'Unknown Region',
      district: newUserDistrict || undefined,
      districtId: matchedDistrict?.id,
      school: matchedSchool ? matchedSchool.name : (newUserSchool || undefined),
      schoolId: newUserSchool || undefined,
      avatar: initials,
      profileComplete: true,
      createdAt: new Date().toISOString(),
      firstLogin: true,
      requiresPasswordChange: true,
      notificationPrefs: {
        standardAlerts: true,
        complianceNode: true,
        nightlyBlackout: false,
        schoolHourBlackout: false
      }
    };

    await createAuthenticatedUser(newUserEmail, newUserPassword, profile);
    
    // Clear state triggers
    setNewUserName('');
    setNewUserEmail('');
    setNewUserPassword('');
    setNewUserRole('student');
    setIsAddUserOpen(false);
  };

  const currentDistricts = getDistrictsForTenantDynamic(newUserTenantId);
  const currentSchools = getSchoolsForTenantDynamic(newUserTenantId);

  React.useEffect(() => {
    if (['superadmin', 'organization'].includes(newUserRole)) {
      setNewUserDistrict('');
      setNewUserSchool('');
    } else {
      const districts = getDistrictsForTenantDynamic(newUserTenantId);
      const schools = getSchoolsForTenantDynamic(newUserTenantId);
      setNewUserDistrict((districts && districts[0]) ? (districts[0].name || '') : '');
      setNewUserSchool((schools && schools[0]) ? (schools[0].id || '') : '');
    }
  }, [newUserTenantId, newUserRole, allDistricts, allSchools]);

  // Derived filtered customer list
  const filteredUsersList = useMemo(() => {
    return (users || []).filter(user => {
      if (!user || !user.uid) return false;
      // Include all users if 'ALL' or 'global' filter is selected
      if (selectedRegionFilter !== 'ALL' && selectedRegionFilter !== 'global' && user.tenantId !== selectedRegionFilter) {
        return false;
      }
      if (selectedRoleFilter !== 'ALL' && user.role !== selectedRoleFilter) {
        return false;
      }
      if (searchUserQuery.trim()) {
        const query = searchUserQuery.toLowerCase();
        const matchesName = user.name && user.name.toLowerCase().includes(query);
        const matchesEmail = user.email && user.email.toLowerCase().includes(query);
        return !!(matchesName || matchesEmail);
      }
      return true;
    });
  }, [users, selectedRegionFilter, selectedRoleFilter, searchUserQuery]);

  try {
    return (
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden p-6 space-y-6">
        {/* Header operations with query fields */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-6 border-b border-slate-100">
          <div>
            <h2 className="text-xl md:text-2xl font-black text-[#011d3c] tracking-tight uppercase">Global User Registry</h2>
          <div className="text-[10px] font-extrabold text-slate-400 tracking-widest uppercase mt-0.5">
            AUDIT ACCOUNTS ACROSS ALL TENANTS.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setIsAddUserOpen(!isAddUserOpen)}
            className="bg-[#0094c8] hover:bg-[#007ba6] text-white px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider flex items-center space-x-1.5 shadow-xs transition-colors cursor-pointer"
          >
            <span>{isAddUserOpen ? 'Close Provision' : 'Provision Account'}</span>
          </button>

          <div className="relative">
            <select
              value={selectedRegionFilter}
              onChange={(e) => setSelectedRegionFilter(e.target.value)}
              className="appearance-none bg-slate-50 border border-slate-200 hover:bg-slate-100 rounded-xl pl-4 pr-10 py-2.5 text-xs font-black uppercase tracking-wider text-slate-800 outline-none focus:ring-2 focus:ring-[#0094c8] cursor-pointer min-w-[160px]"
            >
              <option value="ALL">ALL REGIONS</option>
              {tenants.filter(t => t.id !== 'global').map(t => (
                <option key={t.id} value={t.id}>{typeof t.name === 'string' ? t.name.toUpperCase() : 'UNNAMED'}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 text-slate-500 absolute right-3 top-3 pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={selectedRoleFilter}
              onChange={(e) => setSelectedRoleFilter(e.target.value)}
              className="appearance-none bg-slate-50 border border-slate-200 hover:bg-slate-100 rounded-xl pl-4 pr-10 py-2.5 text-xs font-black uppercase tracking-wider text-slate-800 outline-none focus:ring-2 focus:ring-[#0094c8] cursor-pointer min-w-[150px]"
            >
              <option value="ALL">ALL ROLES</option>
              <option value="student">STUDENT</option>
              <option value="organization">EMPLOYER</option>
              <option value="advisor">ADVISOR</option>
              <option value="parent">PARENT</option>
              <option value="regional_admin">REGIONAL ADMIN</option>
              <option value="superadmin">SUPERADMIN</option>
            </select>
            <ChevronDown className="w-4 h-4 text-slate-500 absolute right-3 top-3 pointer-events-none" />
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search user accounts..."
              value={searchUserQuery}
              onChange={(e) => setSearchUserQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] outline-none placeholder-slate-400 bg-white"
            />
          </div>
        </div>
      </div>

      {/* Creation block */}
      {isAddUserOpen && (
        <form onSubmit={handleCreateUser} className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 space-y-4">
          <div className="flex items-center space-x-2 border-b border-slate-200 pb-2">
            <Sparkles className="w-4 h-4 text-[#0094c8]" />
            <h3 className="text-xs font-black uppercase tracking-wider text-[#011d3c]">Provision Corporate Portal Account</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-500">Full Name</label>
              <input
                type="text"
                placeholder="e.g. Serena Foster"
                value={newUserName}
                required
                onChange={(e) => setNewUserName(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white outline-none focus:ring-2 focus:ring-[#0094c8]"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-500">Email Address</label>
              <input
                type="email"
                placeholder="e.g. advisor@wblportal.edu"
                value={newUserEmail}
                required
                onChange={(e) => setNewUserEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white outline-none focus:ring-2 focus:ring-[#0094c8]"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-500">Temporary Password</label>
              <input
                type="password"
                placeholder="********"
                value={newUserPassword}
                required
                onChange={(e) => setNewUserPassword(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white outline-none focus:ring-2 focus:ring-[#0094c8]"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-500">System Role Map</label>
              <select
                value={newUserRole}
                onChange={(e) => setNewUserRole(e.target.value as any)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white outline-none focus:ring-2 focus:ring-[#0094c8] uppercase font-bold"
              >
                <option value="student">STUDENT</option>
                <option value="organization">ORGANIZATION</option>
                <option value="advisor">ADVISOR</option>
                <option value="parent">PARENT</option>
                <option value="regional_admin">REGIONAL ADMIN</option>
                <option value="superadmin">SUPERADMIN</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-500">Regional Tenant</label>
              <select
                value={newUserTenantId}
                onChange={(e) => setNewUserTenantId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white outline-none focus:ring-2 focus:ring-[#0094c8] uppercase font-bold"
              >
                {tenants.map(t => (
                  <option key={t.id} value={t.id}>{typeof t.name === 'string' ? t.name.toUpperCase() : 'UNNAMED'}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-500">School District Constraints</label>
              <select
                value={newUserDistrict}
                disabled={['superadmin', 'organization'].includes(newUserRole)}
                onChange={(e) => setNewUserDistrict(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white outline-none focus:ring-2 focus:ring-[#0094c8] uppercase font-bold disabled:bg-slate-100 disabled:text-slate-400"
              >
                <option value="">No District Assignment</option>
                {currentDistricts.map(d => (
                  <option key={d.id} value={d.name}>{typeof d.name === 'string' ? d.name : 'UNNAMED'}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase text-slate-500">Active High School</label>
              <select
                value={newUserSchool}
                disabled={['superadmin', 'organization'].includes(newUserRole)}
                onChange={(e) => setNewUserSchool(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white outline-none focus:ring-2 focus:ring-[#0094c8] uppercase font-bold disabled:bg-slate-100 disabled:text-slate-400"
              >
                <option value="">No School Assignment</option>
                {currentSchools.map(s => (
                  <option key={s.id} value={s.id}>{typeof s.name === 'string' ? s.name : 'UNNAMED'}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              className="bg-[#0094c8] hover:bg-[#007ba6] text-white px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-colors shadow-sm cursor-pointer"
            >
              Add Profile to Directory
            </button>
          </div>
        </form>
      )}      {/* Main Ledger Database */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">User Identity</th>
              <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-[#011d3c]/40 text-center">System Role</th>
              <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-[#011d3c]/40 text-center">Primary Region</th>
              <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-[#011d3c]/40">Affiliation &amp; Scope</th>
              <th className="py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-[#011d3c]/40 text-right pr-4 text-center">Manage Configuration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100/60">
            {filteredUsersList.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-xs text-slate-400 uppercase font-black tracking-wider">
                  No matching user accounts registered.
                </td>
              </tr>
            ) : (
              filteredUsersList.map((user) => {
                if (!user) return null;
                const uidStr = user.uid || '';
                const roleStr = typeof user.role === 'string' ? user.role : 'student';
                const tenantIdStr = typeof user.tenantId === 'string' ? user.tenantId : 'global';
                const extendedRegionsArr = Array.isArray(user.extendedRegions) ? user.extendedRegions : [];
                const schoolsServedIdsArr = Array.isArray(user.schoolsServedIds) ? user.schoolsServedIds : [];
                const schoolsServedArr = Array.isArray(user.schoolsServed) ? user.schoolsServed : [];

                const userDistricts = getDistrictsForTenantDynamic(tenantIdStr) || [];
                const isMultiRegionAllowed = ['superadmin', 'regional_admin', 'organization'].includes(roleStr);
                const userRegions = isMultiRegionAllowed 
                  ? [tenantIdStr, ...extendedRegionsArr].filter(Boolean)
                  : [tenantIdStr].filter(Boolean);
                const userSchools = (allSchools || []).filter(s => s && s.regionId && userRegions.includes(s.regionId));
                userSchools.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                const status = saveStatus[uidStr];

                return (
                  <React.Fragment key={uidStr}>
                    <tr className="hover:bg-slate-50/40 transition-colors group">
                      {/* Column 1: Identity */}
                      <td className="py-4 pr-3 pl-1">
                        <div className="flex items-center space-x-3">
                          <div className="relative w-10 h-10 rounded-xl overflow-hidden bg-slate-100 border border-slate-250 flex items-center justify-center flex-shrink-0 font-bold shadow-2xs">
                            <StudentAvatar studentId={uidStr} studentName={user.name || 'Unnamed'} studentEmail={user.email} />
                          </div>
                          <div>
                            <div className="text-xs font-black text-slate-800 flex items-center space-x-2">
                              <span>{typeof user.name === 'string' ? user.name : 'UNNAMED'}</span>
                              {status === 'saving' && (
                                <RefreshCw className="w-3 h-3 text-[#0094c8] animate-spin" />
                              )}
                              {status === 'saved' && (
                                <Check className="w-3 h-3 text-emerald-500" />
                              )}
                            </div>
                            <div className="text-[10px] font-mono text-slate-400 font-semibold mt-0.5">{typeof user.email === 'string' ? user.email : 'NO EMAIL'}</div>
                          </div>
                        </div>
                      </td>

                      {/* Column 2: System Role static badge */}
                      <td className="py-4 pr-3 text-center">
                        <span className={`px-2.5 py-1 rounded-lg border text-[9px] font-black uppercase tracking-wider ${
                          roleStr === 'superadmin' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                          roleStr === 'regional_admin' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                          roleStr === 'organization' ? 'bg-[#0094c8]/5 text-[#0094c8] border-[#0094c8]/20' :
                          roleStr === 'advisor' ? 'bg-cyan-50 text-cyan-800 border-cyan-200' :
                          roleStr === 'student' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          'bg-slate-50 text-slate-700 border-slate-200'
                        }`}>
                          {roleStr === 'regional_admin' ? 'REGIONAL ADMIN' : roleStr === 'organization' ? 'EMPLOYER' : roleStr.toUpperCase()}
                        </span>
                      </td>

                      {/* Column 3: Primary Region static label */}
                      <td className="py-4 pr-3 text-center">
                        <span className="px-2 py-1 bg-slate-150 rounded text-[9px] font-bold text-slate-600 uppercase font-mono">
                          {tenantIdStr ? tenantIdStr.toUpperCase() : 'GLOBAL'}
                        </span>
                      </td>

                      {/* Column 4: Affiliation / Scope Summary */}
                      <td className="py-4 pr-3">
                        {['superadmin', 'organization'].includes(roleStr) ? (
                          <span className="text-slate-400 font-black text-[9px] tracking-widest uppercase">Global Access</span>
                        ) : roleStr === 'advisor' ? (
                          <div className="text-[10px] text-slate-600">
                            <span className="font-extrabold text-slate-400 uppercase tracking-wider text-[8px] mr-1">Serving:</span>
                            <span className="font-bold text-cyan-800 uppercase">
                              {schoolsServedIdsArr.length + (user.schoolId ? 1 : 0)} High School(s)
                            </span>
                            {user.school && (
                              <div className="text-[8px] text-slate-400 truncate max-w-[200px] mt-0.5 uppercase">Primary: {user.school}</div>
                            )}
                          </div>
                        ) : (
                          <div className="text-[10px] text-slate-600 space-y-0.5">
                            <div className="truncate max-w-[240px]">
                              <span className="font-extrabold text-slate-400 uppercase tracking-wider text-[8px] mr-1">District:</span>
                              <span className="font-bold text-slate-700 uppercase">{user.district || 'Unassigned'}</span>
                            </div>
                            <div className="truncate max-w-[240px]">
                              <span className="font-extrabold text-slate-400 uppercase tracking-wider text-[8px] mr-1">School:</span>
                              <span className="font-bold text-slate-700 uppercase">{user.school || 'Unassigned'}</span>
                            </div>
                          </div>
                        )}
                      </td>

                      {/* Column 5: Configure Toggle */}
                      <td className="py-4 text-center pr-4">
                        <button
                          type="button"
                          onClick={() => setExpandedUserUid(expandedUserUid === uidStr ? null : uidStr)}
                          className={`inline-flex px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider items-center space-x-1.5 transition-all cursor-pointer border ${
                            expandedUserUid === uidStr
                              ? 'bg-slate-100 border-slate-300 text-slate-700'
                              : 'bg-[#0094c8]/5 hover:bg-[#0094c8]/10 border-[#0094c8]/10 text-[#0094c8]'
                          }`}
                        >
                          <span>{expandedUserUid === uidStr ? 'Close' : 'Configure'}</span>
                          {expandedUserUid === uidStr ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                    </tr>

                    {/* Expandable Accordion Panel for this specific user */}
                    {expandedUserUid === uidStr && (
                      <tr className="bg-slate-50/70">
                        <td colSpan={5} className="p-6 border-b border-slate-200">
                          <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-6 space-y-6 animate-fade-in text-left">
                            {/* Accordion Title / Status Feedback */}
                            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                              <div className="flex items-center space-x-2">
                                <Settings className="w-4 h-4 text-[#0094c8]" />
                                <h4 className="text-xs font-black uppercase tracking-wider text-[#011d3c]">
                                  Configure Profile &amp; Affiliate Relationships: <span className="text-[#0094c8]">{user.name}</span>
                                </h4>
                              </div>
                              <div className="flex items-center space-x-2">
                                {status === 'saving' && (
                                  <span className="text-[10px] font-bold text-[#0094c8] flex items-center space-x-1">
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                    <span>SAVING CHANGES...</span>
                                  </span>
                                )}
                                {status === 'saved' && (
                                  <span className="text-[10px] font-bold text-emerald-600 flex items-center space-x-1">
                                    <Check className="w-3 h-3" />
                                    <span>ALL CHANGES PERSISTED</span>
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* 4-Column Config Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-xs">
                              
                              {/* Box 1: Core Profile Info & Role */}
                              <div className="space-y-4 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                                <div className="flex items-center space-x-1 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1 border-b border-slate-100 pb-1.5">
                                  <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
                                  <span>Identity &amp; Role</span>
                                </div>

                                <div className="space-y-1">
                                  <label className="text-[9px] font-black uppercase text-slate-500">System Role</label>
                                  <div className="relative">
                                    <select
                                      value={roleStr}
                                      onChange={(e) => handleUpdateUser(uidStr, { role: e.target.value })}
                                      className="w-full appearance-none bg-white border border-slate-200 text-xs font-bold text-slate-700 px-3 py-2 rounded-xl outline-none focus:ring-1 focus:ring-[#0094c8]"
                                    >
                                      <option value="student">STUDENT</option>
                                      <option value="organization">EMPLOYER</option>
                                      <option value="advisor">ADVISOR</option>
                                      <option value="parent">PARENT</option>
                                      <option value="regional_admin">REGIONAL ADMIN</option>
                                      <option value="superadmin">SUPERADMIN</option>
                                    </select>
                                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-2.5 pointer-events-none" />
                                  </div>
                                </div>

                                <div className="space-y-1">
                                  <label className="text-[9px] font-black uppercase text-slate-500">Primary Tenant / Region</label>
                                  <div className="relative">
                                    <select
                                      value={tenantIdStr}
                                      onChange={(e) => {
                                        const selectedTenant = tenants.find(t => t.id === e.target.value);
                                        handleUpdateUser(uidStr, { 
                                          tenantId: e.target.value,
                                          region: selectedTenant ? selectedTenant.name : 'Unknown Region',
                                          district: '',
                                          districtId: '',
                                          school: '',
                                          schoolId: ''
                                        });
                                      }}
                                      className="w-full appearance-none bg-white border border-slate-200 text-xs font-bold text-slate-700 px-3 py-2 rounded-xl outline-none focus:ring-1 focus:ring-[#0094c8]"
                                    >
                                      <option value="global">GLOBAL</option>
                                      {tenants.filter(t => t.id !== 'global').map((tenant) => (
                                        <option key={tenant.id} value={tenant.id}>
                                          {(tenant.name || tenant.id).toUpperCase()}
                                        </option>
                                      ))}
                                    </select>
                                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-2.5 pointer-events-none" />
                                  </div>
                                </div>
                              </div>

                              {/* Box 2: Regional Access Permissions (The Access Tab function) */}
                              <div className="space-y-4 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                                <div className="flex items-center space-x-1 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1 border-b border-slate-100 pb-1.5">
                                  <Key className="w-3.5 h-3.5 text-[#0094c8]" />
                                  <span>Multi-Region Authority</span>
                                </div>

                                {isMultiRegionAllowed ? (
                                  <div className="space-y-2">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">Select authorized region scope:</p>
                                    <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
                                      {tenants.filter(t => t.id !== 'global').map(tenant => {
                                        const isPrimary = tenantIdStr === tenant.id;
                                        const isExtra = extendedRegionsArr.includes(tenant.id);
                                        const isChecked = isPrimary || isExtra;
                                        
                                        return (
                                          <label 
                                            key={tenant.id} 
                                            className={`flex items-center justify-between p-1.5 rounded-lg border text-[10px] cursor-pointer transition-all ${
                                              isPrimary 
                                                ? 'bg-blue-50 border-blue-100 text-blue-700 font-extrabold cursor-not-allowed opacity-80' 
                                                : isChecked 
                                                  ? 'bg-sky-50 border-sky-100 text-sky-800 font-bold' 
                                                  : 'bg-white border-slate-150 text-slate-600 hover:bg-slate-50'
                                            }`}
                                          >
                                            <div className="flex items-center space-x-2 min-w-0">
                                              <input 
                                                type="checkbox" 
                                                checked={isChecked}
                                                disabled={isPrimary}
                                                onChange={() => {
                                                  const currentRegions = extendedRegionsArr;
                                                  let nextRegions;
                                                  if (currentRegions.includes(tenant.id)) {
                                                    nextRegions = currentRegions.filter(id => id !== tenant.id);
                                                  } else {
                                                    nextRegions = [...currentRegions, tenant.id];
                                                  }
                                                  handleUpdateUser(uidStr, { extendedRegions: nextRegions });
                                                }}
                                                className="rounded border-slate-300 text-[#0094c8] focus:ring-[#0094c8] w-3.5 h-3.5 shrink-0" 
                                              />
                                              <span className="truncate uppercase tracking-tight text-[9px] font-bold">{tenant.name}</span>
                                            </div>
                                            {isPrimary && <span className="text-[7px] bg-blue-100 px-1 py-0.5 rounded text-blue-800 tracking-tighter font-black">PRIMARY</span>}
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center justify-center h-28 text-center text-slate-400 p-2 border border-dashed border-slate-200 rounded-lg bg-white">
                                    <Info className="w-4 h-4 text-slate-300 mb-1" />
                                    <span className="text-[9px] uppercase font-black tracking-wider leading-tight">Single Region Only</span>
                                    <span className="text-[8px] text-slate-400 mt-0.5 leading-snug">Multi-region access is only for corporate level administrators.</span>
                                  </div>
                                )}
                              </div>

                              {/* Box 3: Educational Affiliation (District & Primary School) */}
                              <div className="space-y-4 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                                <div className="flex items-center space-x-1 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1 border-b border-slate-100 pb-1.5">
                                  <BookOpen className="w-3.5 h-3.5 text-slate-400" />
                                  <span>School Affiliation Scope</span>
                                </div>

                                {['superadmin', 'organization'].includes(roleStr) ? (
                                  <div className="flex flex-col items-center justify-center h-28 text-center text-slate-400 p-2 border border-dashed border-slate-200 rounded-lg bg-white">
                                    <Info className="w-4 h-4 text-slate-300 mb-1" />
                                    <span className="text-[9px] uppercase font-black tracking-wider">Unrestricted Scope</span>
                                    <span className="text-[8px] text-slate-400 mt-0.5 leading-snug">This system or employer level account is not bound by educational hierarchies.</span>
                                  </div>
                                ) : (
                                  <div className="space-y-3">
                                    <div className="space-y-1">
                                      <label className="text-[9px] font-black uppercase text-slate-500">School District</label>
                                      <div className="relative">
                                        <select
                                          value={user.district || ''}
                                          onChange={(e) => {
                                            const distName = e.target.value;
                                            const distObj = (userDistricts || []).find(d => d && d.name === distName);
                                            handleUpdateUser(uidStr, { 
                                              district: distName,
                                              districtId: distObj?.id || '',
                                              school: '',
                                              schoolId: ''
                                            });
                                          }}
                                          className="w-full appearance-none bg-white border border-slate-200 text-xs font-bold text-slate-700 px-3 py-2 rounded-xl outline-none focus:ring-1 focus:ring-[#0094c8] uppercase"
                                        >
                                          <option value="">NONE / UNASSIGNED</option>
                                          {(userDistricts || []).map((d) => (
                                            <option key={d.id} value={d.name}>{(d.name || '').toUpperCase()}</option>
                                          ))}
                                        </select>
                                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-2.5 pointer-events-none" />
                                      </div>
                                    </div>

                                    <div className="space-y-1">
                                      <label className="text-[9px] font-black uppercase text-slate-500">Primary High School</label>
                                      <div className="relative">
                                        <select
                                          value={user.schoolId || ''}
                                          onChange={(e) => {
                                            const sId = e.target.value;
                                            const schoolObj = (userSchools || []).find(s => s && s.id === sId);
                                            handleUpdateUser(uidStr, { 
                                              schoolId: sId,
                                              school: schoolObj ? schoolObj.name : ''
                                            });
                                          }}
                                          className="w-full appearance-none bg-white border border-slate-200 text-xs font-bold text-slate-700 px-3 py-2 rounded-xl outline-none focus:ring-1 focus:ring-[#0094c8] uppercase"
                                        >
                                          <option value="">NONE / UNASSIGNED</option>
                                          {(userSchools || []).map((s) => (
                                            <option key={s.id} value={s.id}>{(s.name || '').toUpperCase()}</option>
                                          ))}
                                        </select>
                                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-2.5 pointer-events-none" />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Box 4: Advisor School Assignments (Attaching & Detaching) */}
                              <div className="space-y-4 bg-slate-50/50 p-4 rounded-xl border border-slate-100">
                                <div className="flex items-center space-x-1 text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1 border-b border-slate-100 pb-1.5">
                                  <Users className="w-3.5 h-3.5 text-cyan-600" />
                                  <span>Served High Schools</span>
                                </div>                                 {roleStr === 'advisor' ? (
                                  <div className="space-y-3">
                                    {/* School Selector Dropdown to Attach */}
                                    <div className="space-y-1">
                                      <label className="text-[9px] font-black uppercase text-slate-500">Attach Active School</label>
                                      <div className="flex gap-1.5">
                                        <div className="relative flex-1">
                                          <select
                                            value={selectedSchoolForAdvisor[uidStr] || ''}
                                            onChange={(e) => setSelectedSchoolForAdvisor(prev => ({ ...prev, [uidStr]: e.target.value }))}
                                            className="w-full appearance-none bg-white border border-slate-200 text-[10px] font-bold text-slate-700 px-2.5 py-1.5 rounded-xl outline-none focus:ring-1 focus:ring-[#0094c8] uppercase"
                                          >
                                            <option value="">Select...</option>
                                            {(allSchools || [])
                                              .filter(s => s && (!user.schoolId || user.schoolId !== s.id))
                                              .filter(s => s && !schoolsServedIdsArr.includes(s.id))
                                              .map((s) => (
                                                <option key={s.id} value={s.id}>{(s.name || '').toUpperCase()}</option>
                                              ))}
                                          </select>
                                          <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2 top-2 pointer-events-none" />
                                        </div>
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            const schoolIdToAttach = selectedSchoolForAdvisor[uidStr];
                                            if (!schoolIdToAttach) return;
                                            const foundSchool = (allSchools || []).find(s => s && s.id === schoolIdToAttach);
                                            if (!foundSchool) return;

                                            const currentServedIds = schoolsServedIdsArr;
                                            const currentServedNames = schoolsServedArr;

                                            await handleUpdateUser(uidStr, {
                                              schoolsServedIds: [...currentServedIds, schoolIdToAttach],
                                              schoolsServed: [...currentServedNames, foundSchool.name]
                                            });

                                            setSelectedSchoolForAdvisor(prev => {
                                              const copy = { ...prev };
                                              delete copy[uidStr];
                                              return copy;
                                            });
                                          }}
                                          className="bg-cyan-600 hover:bg-cyan-700 text-white px-2.5 rounded-xl text-[10px] font-black uppercase transition-all shrink-0 cursor-pointer"
                                        >
                                          Attach
                                        </button>
                                      </div>
                                    </div>

                                    {/* Display Current Served Schools list */}
                                    <div className="space-y-1">
                                      <label className="text-[9px] font-black uppercase text-slate-500">Currently Serving ({ schoolsServedIdsArr.length + (user.schoolId ? 1 : 0) })</label>
                                      <div className="space-y-1 max-h-24 overflow-y-auto pr-1">
                                        {/* Primary School if set */}
                                        {user.schoolId && (
                                          <div className="flex items-center justify-between bg-emerald-50 border border-emerald-100 p-1.5 rounded-lg text-[9px] font-bold text-emerald-800 uppercase font-mono">
                                            <span className="truncate">Primary: {user.school || user.schoolId}</span>
                                            <span className="text-[7px] bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded font-black shrink-0 ml-1">PRIMARY</span>
                                          </div>
                                        )}
                                        {/* Schools Served IDs */}
                                        {schoolsServedIdsArr.map((id, index) => {
                                          const name = schoolsServedArr[index] || id;
                                          return (
                                            <div key={`${id}-${index}`} className="flex items-center justify-between bg-white border border-slate-200 p-1.5 rounded-lg text-[9px] font-semibold text-slate-700 uppercase font-mono">
                                              <span className="truncate">{name}</span>
                                              <button
                                                type="button"
                                                onClick={async () => {
                                                  const nextServedIds = schoolsServedIdsArr.filter(item => item !== id);
                                                  const nextServedNames = schoolsServedArr.filter((item, idx) => idx !== index);
                                                  await handleUpdateUser(uidStr, {
                                                    schoolsServedIds: nextServedIds,
                                                    schoolsServed: nextServedNames
                                                  });
                                                }}
                                                className="text-slate-400 hover:text-rose-600 p-0.5 cursor-pointer"
                                                title="Remove assignment"
                                              >
                                                <X className="w-3 h-3" />
                                              </button>
                                            </div>
                                          );
                                        })}
                                        {!user.schoolId && schoolsServedIdsArr.length === 0 && (
                                          <p className="text-[10px] text-slate-400 italic text-center py-2">No schools assigned.</p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center justify-center h-28 text-center text-slate-400 p-2 border border-dashed border-slate-200 rounded-lg bg-white">
                                    <Info className="w-4 h-4 text-slate-300 mb-1" />
                                    <span className="text-[9px] uppercase font-black tracking-wider">Advisors Only</span>
                                    <span className="text-[8px] text-slate-400 mt-0.5 leading-snug">Served school tracking is only applicable to Advisor accounts.</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Footer actions of accordion */}
                            <div className="flex items-center justify-between border-t border-slate-100 pt-4">
                              <button
                                type="button"
                                onClick={() => handleDeleteUser(uidStr, user.name)}
                                className="flex items-center space-x-1 px-3 py-2 border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>Delete User Profile</span>
                              </button>
                              
                              <button
                                type="button"
                                onClick={() => setExpandedUserUid(null)}
                                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer border border-slate-200"
                              >
                                Collapse Settings
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
    );
  } catch (err: any) {
    console.error("Registry rendering error:", err);
    return (
      <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm text-center max-w-2xl mx-auto mt-12 space-y-4">
        <div className="w-16 h-16 bg-rose-50 border border-rose-100 rounded-2xl flex items-center justify-center mx-auto animate-bounce">
          <Info className="w-8 h-8 text-rose-500" />
        </div>
        <h2 className="text-lg font-black uppercase tracking-tight text-[#011d3c]">Registry Tab Error</h2>
        <p className="text-xs text-slate-500 leading-relaxed max-w-md mx-auto">
          The User Registry encountered an evaluation error during list rendering or data sorting:
        </p>
        <div className="bg-slate-50 border border-slate-150 text-left p-4 rounded-xl text-[10px] font-mono text-rose-600 max-h-60 overflow-y-auto whitespace-pre-wrap">
          {err.message || err.toString()}
          {"\n\nStack Trace:\n"}
          {err.stack}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-sm cursor-pointer"
        >
          Reload Dashboard
        </button>
      </div>
    );
  }
};

export const DEFAULT_PERSONA_SECTIONS: PersonaSection[] = [
  {
    id: 'student',
    label: 'STUDENT HUB',
    title: 'Launch Your Career',
    description: 'Step into the professional arena with total clarity. Explore modern work-based learning pathways tailored to your skills, secure mentored internships, earn industry-recognized credentials, and begin building a powerhouse career file before graduation.',
    quote: '"This experience gives me actual industry-grade practice before starting medical school."',
    author: 'Alex Rivera',
    authorInitials: 'AR',
    authorDetails: 'Allied Health Intern • ApexTech Medical',
    items: [
      'Discover premium local internships matching your interests',
      'Gain digital badges to certify high-demand skills',
      'Log program hours with seamless mobile timesheets'
    ]
  },
  {
    id: 'employer',
    label: 'EMPLOYER PLATFORM',
    title: 'Secure Your Pipeline',
    description: 'Connect your business straight to local classrooms. Post internship opportunities, review verified student applications, approve mobile timesheets, evaluate safety guidelines, and recruit highly motivated, pre-screened regional talent instantly.',
    quote: '"The hands-on co-op placement gives our business an active, pre-vetted local talent pool."',
    author: 'Vince K.',
    authorInitials: 'BM',
    authorDetails: 'Senior Assembly Lead • BMW Manufacturing Hub',
    items: [
      'Host students in flexible, academic-backed placements',
      'Simplify tracking with secure, one-click evaluation logs',
      'Boost community visibility as an elite education partner'
    ]
  },
  {
    id: 'parent',
    label: 'PARENT GUARDIAN PORTAL',
    title: 'Guide your Student',
    description: "Be an active participant in your child's success. Track safety compliance protocols, view real-time logbooks, review training agreements, grant worksite permissions, and communicate and align directly with school coordinators on a secure, clear platform.",
    quote: '"The portal gives me absolute peace of mind knowing my daughter\'s clinical internship is fully vetted and safe."',
    author: 'Maria L.',
    authorInitials: 'ML',
    authorDetails: 'Parent Guardian • Allied Health Track',
    items: [
      'Ensure placements meet high safety and labor standards',
      'Review and authorize internship agreements with ease',
      'Monitor timesheets, feedback, and milestones in real-time'
    ]
  },
  {
    id: 'advisor',
    label: 'ADVISOR CENTRAL',
    title: 'Streamline Success',
    description: 'Coordinate and align educational pathways perfectly with industry requirements. Easily matching certified students with host employers, routing permission documents to parents, endorsing learning logs, and viewing high-impact analytical metrics.',
    quote: '"Unifying students, employers, and schools creates a massive competitive advantage for our county\'s future."',
    author: 'Teresa C.',
    authorInitials: 'TC',
    authorDetails: 'Regional CTE Pathways Director',
    items: [
      'Track student compliance and safety standards countywide',
      'Digitally manage evaluation agreements and timesheet reviews',
      'Foster real connection between business hosts and schools'
    ]
  }
];

export const SuperadminConsole: React.FC = () => {
  const { showAlert, showConfirm, showToast } = useInAppDialog();
  const { tenants, updateTenantBranding, updateAllTenantsBranding, updateGlobalBranding, globalBranding, setCurrentView, opportunities, users, logs, updateUserProfile } = useWBL();
  const [activeTab, setActiveTab] = useState<'USERS' | 'OPPORTUNITIES_APPS' | 'EVENTS' | 'NOTIFICATIONS' | 'ACCESS' | 'PORTALS' | 'CURRICULUM' | 'TUTORIALS' | 'METADATA' | 'IDENTITY' | 'IMAGERY' | 'COMPLIANCE' | 'CONFIG' | 'CAREER'>(() => {
    const stored = sessionStorage.getItem('superadminConsoleTab');
    return (stored && stored !== 'REGISTRY' ? stored : 'USERS') as any;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPortalCounty, setSelectedPortalCounty] = useState<any | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState<'on' | 'off' | null>(null);
  const [bulkVisibleConfirm, setBulkVisibleConfirm] = useState<'on' | 'off' | null>(null);
  const [editingFoundationId, setEditingFoundationId] = useState<string | null>(null);
  const [editingPersonaCounty, setEditingPersonaCounty] = useState<any>(null);
  const [editingSections, setEditingSections] = useState<PersonaSection[]>([]);
  const [activeSectionTab, setActiveSectionTab] = useState<'student' | 'employer' | 'parent' | 'advisor'>('student');
  const [uploadingImageSection, setUploadingImageSection] = useState<string | null>(null);
  const [personaUploadProgress, setPersonaUploadProgress] = useState<number | null>(null);

  useEffect(() => {
    if (editingPersonaCounty) {
      const tenantMatch = tenants.find(t => t.id === editingPersonaCounty.id);
      const initial = tenantMatch?.personaSections || JSON.parse(JSON.stringify(DEFAULT_PERSONA_SECTIONS));
      setEditingSections(initial);
      setActiveSectionTab('student');
    }
  }, [editingPersonaCounty, tenants]);

  useEffect(() => {
    sessionStorage.setItem('superadminConsoleTab', activeTab);
    setEditingFoundationId(null);
  }, [activeTab]);

  useEffect(() => {
    const handleTabChange = () => {
      const storedTab = sessionStorage.getItem('superadminConsoleTab');
      if (storedTab && storedTab !== activeTab && storedTab !== 'REGISTRY') {
        setActiveTab(storedTab as any);
      }
    };
    window.addEventListener('superadmin-tab-change', handleTabChange);
    return () => window.removeEventListener('superadmin-tab-change', handleTabChange);
  }, [activeTab]);

  const handlePersonaImageUpload = (sectionId: string, file: File) => {
    if (!editingPersonaCounty) return;
    setUploadingImageSection(sectionId);
    setPersonaUploadProgress(0);

    const fileName = `branding/${editingPersonaCounty.id}-persona-${sectionId}-${Date.now()}-${file.name}`;
    const storageRef = ref(storage, fileName);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setPersonaUploadProgress(Math.round(progress));
      },
      (err) => {
        console.error("Persona image upload failed:", err);
        showAlert({
          title: "Upload Failed",
          message: `Upload failed: ${err.message}`,
          type: "error"
        });
        setUploadingImageSection(null);
        setPersonaUploadProgress(null);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setEditingSections(prev => prev.map(sec => 
            sec.id === sectionId ? { ...sec, image: downloadURL } : sec
          ));
          setUploadingImageSection(null);
          setPersonaUploadProgress(null);
        } catch (e: any) {
          console.error("Failed to get download URL:", e);
          showAlert({
            title: "Download URL Error",
            message: `Failed to retrieve image link: ${e.message}`,
            type: "error"
          });
          setUploadingImageSection(null);
          setPersonaUploadProgress(null);
        }
      }
    );
  };

  const handleSavePersonaSections = () => {
    if (!editingPersonaCounty) return;
    updateTenantBranding(editingPersonaCounty.id, { personaSections: editingSections });
    setEditingPersonaCounty(null);
  };

  // Console Tabs configurations
  const TABS = [
    { id: 'USERS', label: 'Users', icon: Users },
    { id: 'OPPORTUNITIES_APPS', label: 'Opps & Apps', icon: Briefcase },
    { id: 'EVENTS', label: 'Events', icon: Activity },
    { id: 'NOTIFICATIONS', label: 'Notifications', icon: Bell },
    { id: 'PORTALS', label: 'Portals', icon: Globe }, // Map icon look
    { id: 'CURRICULUM', label: 'Curriculum', icon: BookOpen },
    { id: 'TUTORIALS', label: 'Tutorials', icon: PlayCircle },
    { id: 'METADATA', label: 'Metadata', icon: Database },
    { id: 'IDENTITY', label: 'Identity', icon: Palette },
    { id: 'IMAGERY', label: 'Imagery', icon: ImageIcon },
    { id: 'COMPLIANCE', label: 'Compliance', icon: Scale },
    { id: 'CONFIG', label: 'Config', icon: Settings },
    { id: 'CAREER', label: 'Career', icon: Sparkles },
  ] as const;

  // Filtered list of counties based on search query
  const filteredCounties = useMemo(() => {
    const allCounties = SC_COUNTIES;
    return allCounties.filter(county => 
      county.name.toLowerCase().includes(searchQuery.toLowerCase())
    ).map(county => {
      const tenantMatch = tenants.find(t => t.id === county.id);
      const isActive = tenantMatch ? tenantMatch.isActive === true : ['spartanburg', 'union', 'charleston', 'greenville', 'richland', 'horry', 'lexington', 'beaufort'].includes(county.id);
      const isVisible = tenantMatch ? tenantMatch.isVisible !== false : true;
      const highSchoolActive = tenantMatch ? tenantMatch.highSchoolActive !== false : true;
      const postSecondaryActive = tenantMatch ? tenantMatch.postSecondaryActive !== false : true;
      const foundationsEnabled = tenantMatch ? tenantMatch.foundationsEnabled !== false : true;
      const prohibitedEmailDomains = tenantMatch?.prohibitedEmailDomains || [];
      return {
        ...county,
        isActive,
        isVisible,
        highSchoolActive,
        postSecondaryActive,
        foundationsEnabled,
        prohibitedEmailDomains,
        subdomain: tenantMatch?.subdomain || county.id,
        tenantMatch,
      };
    });
  }, [tenants, searchQuery]);

  const activeSelectedCounty = useMemo(() => {
    if (!selectedPortalCounty) return null;
    return filteredCounties.find(c => c.id === selectedPortalCounty.id) || selectedPortalCounty;
  }, [selectedPortalCounty, filteredCounties]);

  const handleTogglePortal = (countyId: string, currentStatus: boolean, e: React.MouseEvent) => {
    e.stopPropagation(); // Avoid triggering expansion/collapse click
    const nextStatus = !currentStatus;
    updateTenantBranding(countyId, { isActive: nextStatus });
  };

  const handleToggleAllPortals = (activate: boolean) => {
    setBulkConfirm(activate ? 'on' : 'off');
  };

  const executeBulkToggle = async (activate: boolean) => {
    await updateAllTenantsBranding({ isActive: activate });
    setBulkConfirm(null);
  };

  const handleToggleAllVisible = (visible: boolean) => {
    setBulkVisibleConfirm(visible ? 'on' : 'off');
  };

  const executeBulkVisibleToggle = async (visible: boolean) => {
    await updateAllTenantsBranding({ isVisible: visible });
    setBulkVisibleConfirm(null);
  };

  const handleToggleLevel = (countyId: string, level: 'highschool' | 'postsecondary', currentStatus: boolean) => {
    const nextStatus = !currentStatus;
    if (level === 'highschool') {
      updateTenantBranding(countyId, { highSchoolActive: nextStatus });
    } else {
      updateTenantBranding(countyId, { postSecondaryActive: nextStatus });
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f4f8] text-[#011d3c] font-sans pb-16">
      {/* Console Top Header Banner with Back Navigation and Title */}
      <div className="bg-white border-b border-slate-200/80 py-4 px-4 sm:px-6">
        <div className="w-[95%] max-w-[1920px] mx-auto">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="p-2 hover:bg-slate-100 rounded-xl transition-all cursor-pointer text-slate-500 hover:text-slate-900 group"
              title="Return to Dashboard"
              id="console-back-btn"
            >
              <ArrowLeft className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" />
            </button>
            <div className="flex-1">
              
                <span className="bg-[#0094c8]/5 border border-[#0094c8]/20 text-[#0094c8] text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full flex items-center space-x-1 font-mono">
                  <ShieldCheck className="w-3 h-3" />
                  <span>Admin Authority</span>
                </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="w-[95%] max-w-[1920px] mx-auto px-2 sm:px-4 mt-5 space-y-4">
        
        {/* Mockup Tab Ribbon Row */}
        <div className="bg-[#e2e8f0]/60 p-1 border border-slate-200/50 rounded-2xl flex flex-wrap items-center gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isSelected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-2 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1.5 transition-all cursor-pointer rounded-xl ${
                  isSelected
                    ? 'bg-white text-slate-900 shadow-xs border border-slate-200/40'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/40'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-[#0094c8]' : 'text-slate-400'}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content Deck Grid depending on Selected Tab */}
        {editingFoundationId ? (
          <FoundationEditor foundationId={editingFoundationId} onBack={() => setEditingFoundationId(null)} />
        ) : activeTab === 'USERS' ? (
          <UsersTabContent />
        ) : activeTab === 'OPPORTUNITIES_APPS' ? (
          <OpportunitiesAppsTabContent />
        ) : activeTab === 'METADATA' ? (
          <MetadataTabContent />
        ) : activeTab === 'CURRICULUM' ? (
          <CurriculumTabContent onEdit={(id) => setEditingFoundationId(id)} />
        ) : activeTab === 'EVENTS' ? (
          <EventsTabContent logs={logs} />
        ) : activeTab === 'NOTIFICATIONS' ? (
          <NotificationsTabContent />
        ) : activeTab === 'ACCESS' ? (
          <AccessTabContent users={users} tenants={tenants} updateUser={updateUserProfile} />
        ) : activeTab === 'CONFIG' ? (
          <ConfigTabContent />
        ) : activeTab === 'IDENTITY' ? (
          <IdentityTabContent />
        ) : activeTab === 'IMAGERY' ? (
          <ImageryTabContent />
        ) : activeTab === 'CAREER' ? (
          <CareerMappingTabContent />
        ) : activeTab === 'COMPLIANCE' ? (
          <ComplianceTabContent users={users} onSelectUser={(u) => {
            setActiveTab('USERS');
          }} />
        ) : activeTab !== 'PORTALS' ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm text-center max-w-2xl mx-auto mt-12 space-y-4">
            <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center mx-auto">
              <Database className="w-8 h-8 text-[#0094c8] animate-pulse" />
            </div>
            <h2 className="text-lg font-black uppercase tracking-tight text-[#011d3c]">{activeTab} Ledger Protocol</h2>
            <p className="text-xs text-slate-500 leading-relaxed max-w-md mx-auto">
              This panel represents a restricted system ledger module. Under current policy mandates, all regional configurations and visible workspace mappings are actively maintained through the <span className="font-bold text-[#0094c8]">Portals visibility framework</span>.
            </p>
            <button
              onClick={() => setActiveTab('PORTALS')}
              className="px-4 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white font-extrabold text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-sm cursor-pointer"
            >
              Configure Portals
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-md overflow-hidden p-6 space-y-6">
            
            {/* Header Content matching the exact mockup style */}
            <div className="space-y-1 pb-4 border-b border-slate-100">
              <h2 className="text-xl md:text-2xl font-black text-[#011d3c] tracking-tight uppercase">Regional Portals</h2>
              <div className="text-[10px] font-extrabold text-slate-400 tracking-widest uppercase">
                CONTROL VISIBILITY AND ALLOWED EDUCATION LEVELS.
              </div>
            </div>

            {/* Quick Helper Filter/Search bar with Bulk Actions */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-150">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Filter county portal..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] outline-none placeholder-slate-400 bg-white"
                  />
                </div>
                <div className="text-[10px] text-slate-400 font-mono">
                  Showing {filteredCounties.length} of {SC_COUNTIES.length} jurisdictions
                </div>
              </div>

              {/* Bulk Toggle Action Buttons */}
              <div className="flex flex-wrap items-center gap-4">
                {/* Active Bulk Group */}
                <div className="flex items-center gap-2">
                  {bulkConfirm === null ? (
                    <>
                      <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mr-1">Active:</span>
                      <button
                        type="button"
                        id="btn-bulk-turn-on"
                        onClick={() => handleToggleAllPortals(true)}
                        className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-700 hover:text-emerald-800 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all shadow-2xs cursor-pointer flex items-center gap-1.5"
                      >
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span>All On</span>
                      </button>
                      <button
                        type="button"
                        id="btn-bulk-turn-off"
                        onClick={() => handleToggleAllPortals(false)}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 hover:text-slate-800 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all shadow-2xs cursor-pointer flex items-center gap-1.5"
                      >
                        <span className="w-2 h-2 rounded-full bg-slate-400" />
                        <span>All Off</span>
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 bg-rose-50 border border-rose-150 px-3 py-1.5 rounded-xl">
                      <span className="text-[10px] font-bold text-rose-800 uppercase tracking-wide mr-1">
                        Turn ALL {bulkConfirm === 'on' ? 'ON' : 'OFF'}?
                      </span>
                      <button
                        type="button"
                        id="btn-bulk-confirm"
                        onClick={() => executeBulkToggle(bulkConfirm === 'on')}
                        className="px-2 py-1 bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-black uppercase rounded-md cursor-pointer transition-colors shadow-2xs"
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        id="btn-bulk-cancel"
                        onClick={() => setBulkConfirm(null)}
                        className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 text-[10px] font-black uppercase rounded-md cursor-pointer transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>

                {/* Visible Bulk Group */}
                <div className="flex items-center gap-2 border-t md:border-t-0 md:border-l border-slate-200 pt-2 md:pt-0 md:pl-4">
                  {bulkVisibleConfirm === null ? (
                    <>
                      <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mr-1">Visible:</span>
                      <button
                        type="button"
                        id="btn-bulk-visible-on"
                        onClick={() => handleToggleAllVisible(true)}
                        className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-700 hover:text-blue-800 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all shadow-2xs cursor-pointer flex items-center gap-1.5"
                      >
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        <span>Show All</span>
                      </button>
                      <button
                        type="button"
                        id="btn-bulk-visible-off"
                        onClick={() => handleToggleAllVisible(false)}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 hover:text-slate-800 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all shadow-2xs cursor-pointer flex items-center gap-1.5"
                      >
                        <span className="w-2 h-2 rounded-full bg-slate-400" />
                        <span>Hide All</span>
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 bg-rose-50 border border-rose-150 px-3 py-1.5 rounded-xl">
                      <span className="text-[10px] font-bold text-rose-800 uppercase tracking-wide mr-1">
                        Make ALL {bulkVisibleConfirm === 'on' ? 'VISIBLE' : 'HIDDEN'}?
                      </span>
                      <button
                        type="button"
                        id="btn-bulk-visible-confirm"
                        onClick={() => executeBulkVisibleToggle(bulkVisibleConfirm === 'on')}
                        className="px-2 py-1 bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-black uppercase rounded-md cursor-pointer transition-colors shadow-2xs"
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        id="btn-bulk-visible-cancel"
                        onClick={() => setBulkVisibleConfirm(null)}
                        className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 text-[10px] font-black uppercase rounded-md cursor-pointer transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Metrics Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-wider text-slate-400">Total Portals</div>
                  <div className="text-base font-black text-slate-900">{SC_COUNTIES.length}</div>
                </div>
                <Globe className="w-4 h-4 text-slate-400" />
              </div>
              <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-wider text-emerald-700">Active Portals</div>
                  <div className="text-base font-black text-emerald-800">{filteredCounties.filter(c => c.isActive).length}</div>
                </div>
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-wider text-blue-700">Visible on Map</div>
                  <div className="text-base font-black text-blue-800">{filteredCounties.filter(c => c.isVisible).length}</div>
                </div>
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              </div>
              <div className="bg-rose-50/50 border border-rose-100 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="text-[9px] font-black uppercase tracking-wider text-rose-700">Restricted Domains</div>
                  <div className="text-base font-black text-rose-800">
                    {filteredCounties.filter(c => (c.prohibitedEmailDomains?.length || 0) > 0).length} Portals
                  </div>
                </div>
                <ShieldAlert className="w-4 h-4 text-rose-500" />
              </div>
            </div>

            {/* County List Table View */}
            <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-slate-50/90 border-b border-slate-200">
                    <tr className="text-[10px] font-black uppercase tracking-wider text-slate-500 select-none">
                      <th className="py-3.5 px-4">County / Regional Portal</th>
                      <th className="py-3.5 px-4 text-center w-28">Status</th>
                      <th className="py-3.5 px-4 text-center w-28">Visibility</th>
                      <th className="py-3.5 px-4 text-center">Education Levels</th>
                      <th className="py-3.5 px-4">Prohibited Email Domains</th>
                      <th className="py-3.5 px-4">Subdomain</th>
                      <th className="py-3.5 px-4 text-right w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {filteredCounties.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-slate-400">
                          <Globe className="w-8 h-8 text-slate-300 mx-auto mb-2 animate-pulse" />
                          <p className="text-xs font-bold text-slate-600">No regional portals match your filter criteria.</p>
                        </td>
                      </tr>
                    ) : (
                      filteredCounties.map((county) => {
                        const numProhibited = county.prohibitedEmailDomains?.length || 0;
                        return (
                          <tr
                            key={county.id}
                            onClick={() => setSelectedPortalCounty(county)}
                            className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                          >
                            {/* 1. County Name */}
                            <td className="py-3.5 px-4">
                              <div className="flex items-center space-x-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shadow-3xs transition-all ${
                                  county.isActive 
                                    ? "bg-sky-50 border-sky-100 text-[#0094c8]" 
                                    : "bg-slate-100 border-slate-200 text-slate-400"
                                }`}>
                                  <Globe className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="font-extrabold text-slate-900 group-hover:text-[#0094c8] transition-colors uppercase tracking-tight text-xs flex items-center space-x-1.5">
                                    <span>{county.name} County</span>
                                    {county.id === "spartanburg" && (
                                      <span className="px-1.5 py-0.2 bg-amber-50 text-amber-700 border border-amber-200 text-[9px] font-black rounded uppercase">
                                        Primary
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[10px] text-slate-400 font-medium">
                                    South Carolina • ID: {county.id}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* 2. Status with ToggleSwitch */}
                            <td className="py-3.5 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center space-x-2">
                                <span className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-md border ${
                                  county.isActive 
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                                    : "bg-slate-100 text-slate-500 border-slate-200"
                                }`}>
                                  {county.isActive ? "Active" : "Inactive"}
                                </span>
                                <ToggleSwitch 
                                  checked={county.isActive} 
                                  onChange={(val) => updateTenantBranding(county.id, { isActive: val })} 
                                  id={`portal-toggle-active-${county.id}`}
                                />
                              </div>
                            </td>

                            {/* 3. Visibility with ToggleSwitch */}
                            <td className="py-3.5 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center space-x-2">
                                <span className={`px-2 py-0.5 text-[9px] font-black uppercase rounded-md border ${
                                  county.isVisible 
                                    ? "bg-blue-50 text-blue-700 border-blue-200" 
                                    : "bg-slate-100 text-slate-500 border-slate-200"
                                }`}>
                                  {county.isVisible ? "Visible" : "Hidden"}
                                </span>
                                <ToggleSwitch 
                                  checked={county.isVisible} 
                                  onChange={(val) => updateTenantBranding(county.id, { isVisible: val })} 
                                  id={`portal-toggle-visible-${county.id}`}
                                />
                              </div>
                            </td>

                            {/* 4. Education Levels */}
                            <td className="py-3.5 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="inline-flex items-center space-x-1.5">
                                <span
                                  className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border cursor-pointer transition-all ${
                                    county.highSchoolActive
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                      : "bg-slate-50 text-slate-400 border-slate-200 opacity-60"
                                  }`}
                                  onClick={() => handleToggleLevel(county.id, "highschool", county.highSchoolActive)}
                                  title={`High School: ${county.highSchoolActive ? "Enabled" : "Disabled"} (Click to toggle)`}
                                >
                                  HS
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border cursor-pointer transition-all ${
                                    county.postSecondaryActive
                                      ? "bg-sky-50 text-sky-700 border-sky-200"
                                      : "bg-slate-50 text-slate-400 border-slate-200 opacity-60"
                                  }`}
                                  onClick={() => handleToggleLevel(county.id, "postsecondary", county.postSecondaryActive)}
                                  title={`Post-Secondary: ${county.postSecondaryActive ? "Enabled" : "Disabled"} (Click to toggle)`}
                                >
                                  PS
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider border cursor-pointer transition-all ${
                                    county.foundationsEnabled !== false
                                      ? "bg-purple-50 text-purple-700 border-purple-200"
                                      : "bg-slate-50 text-slate-400 border-slate-200 opacity-60"
                                  }`}
                                  onClick={() => updateTenantBranding(county.id, { foundationsEnabled: !(county.foundationsEnabled !== false) })}
                                  title={`Foundations Curriculum: ${county.foundationsEnabled !== false ? "Enabled" : "Disabled"} (Click to toggle)`}
                                >
                                  FND
                                </span>
                              </div>
                            </td>

                            {/* 5. Prohibited Domains */}
                            <td className="py-3.5 px-4">
                              {numProhibited > 0 ? (
                                <div className="flex items-center space-x-1.5 flex-wrap">
                                  <span className="px-2 py-0.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-md font-mono text-[10px] font-bold flex items-center space-x-1">
                                    <ShieldAlert className="w-3 h-3 text-rose-500" />
                                    <span>{numProhibited} {numProhibited === 1 ? "Rule" : "Rules"}</span>
                                  </span>
                                  {county.prohibitedEmailDomains?.slice(0, 2).map((d) => (
                                    <span key={d} className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[9px] font-mono">
                                      @{d}
                                    </span>
                                  ))}
                                  {numProhibited > 2 && (
                                    <span className="text-[9px] text-slate-400 font-bold">
                                      +{numProhibited - 2}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[10px] text-slate-400 italic">
                                  None (Open)
                                </span>
                              )}
                            </td>

                            {/* 6. Subdomain */}
                            <td className="py-3.5 px-4">
                              <span className="font-mono text-[11px] text-[#0094c8] bg-sky-50/60 px-2 py-0.5 rounded border border-sky-100 font-semibold">
                                {county.subdomain}.wbl.sc
                              </span>
                            </td>

                            {/* 7. Actions */}
                            <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => setSelectedPortalCounty(county)}
                                className="px-2.5 py-1.5 bg-slate-100 hover:bg-[#0094c8] hover:text-white text-slate-700 font-extrabold text-[10px] uppercase tracking-wider rounded-lg transition-all shadow-3xs cursor-pointer flex items-center space-x-1 ml-auto"
                                title="Configure Portal Settings & Prohibited Domains"
                              >
                                <Sliders className="w-3 h-3" />
                                <span>Configure</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}

      </div>

      {editingPersonaCounty && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 text-left">
            
            {/* Header */}
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div>
                <h3 className="text-lg font-black text-[#011d3c] uppercase tracking-tight">
                  Configure Landing Page Persona Sections
                </h3>
                <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-widest mt-0.5">
                  {editingPersonaCounty.name} County Customizer
                </p>
              </div>
              <button
                type="button"
                onClick={() => setEditingPersonaCounty(null)}
                className="w-8 h-8 rounded-full bg-white hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Main Body Grid */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              
              {/* Section Tabs Selector */}
              <div className="flex border-b border-slate-200 gap-2">
                {[
                  { id: 'student', label: 'Student Hub', icon: GraduationCap, color: 'text-sky-500 bg-sky-50' },
                  { id: 'employer', label: 'Employer Platform', icon: Briefcase, color: 'text-emerald-500 bg-emerald-50' },
                  { id: 'parent', label: 'Parent Portal', icon: Heart, color: 'text-pink-500 bg-pink-50' },
                  { id: 'advisor', label: 'Advisor Central', icon: Users, color: 'text-violet-500 bg-violet-50' }
                ].map((sec) => {
                  const isTabActive = activeSectionTab === sec.id;
                  const Icon = sec.icon;
                  return (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => setActiveSectionTab(sec.id as any)}
                      className={`px-4 py-3 border-b-2 font-black text-xs uppercase tracking-wider flex items-center gap-2 cursor-pointer transition-all ${
                        isTabActive 
                          ? 'border-[#0094c8] text-[#0094c8] bg-slate-50/30' 
                          : 'border-transparent text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      <div className={`p-1 rounded-md ${sec.color}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <span>{sec.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Current Tab Form Fields */}
              {editingSections.map((sec, idx) => {
                if (sec.id !== activeSectionTab) return null;

                return (
                  <div key={sec.id} className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-2 duration-150 text-left">
                    
                    {/* Left Column: Visual Content (Image, Label, Title) */}
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Section Label/Badge</label>
                        <input
                          type="text"
                          value={sec.label}
                          onChange={(e) => {
                            const updated = [...editingSections];
                            updated[idx].label = e.target.value;
                            setEditingSections(updated);
                          }}
                          className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-black uppercase tracking-wide focus:ring-2 focus:ring-[#0094c8] outline-none"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Section Title</label>
                        <input
                          type="text"
                          value={sec.title}
                          onChange={(e) => {
                            const updated = [...editingSections];
                            updated[idx].title = e.target.value;
                            setEditingSections(updated);
                          }}
                          className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs font-bold focus:ring-2 focus:ring-[#0094c8] outline-none text-[#011d3c]"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Description Narrative</label>
                        <textarea
                          rows={4}
                          value={sec.description}
                          onChange={(e) => {
                            const updated = [...editingSections];
                            updated[idx].description = e.target.value;
                            setEditingSections(updated);
                          }}
                          className="w-full px-3.5 py-2 border border-slate-200 rounded-xl text-xs leading-relaxed focus:ring-2 focus:ring-[#0094c8] outline-none text-slate-600"
                        />
                      </div>

                      {/* Section Image Customizer */}
                      <div className="space-y-3 p-4 bg-slate-50 rounded-2xl border border-slate-150">
                        <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest flex items-center gap-1.5">
                          <ImageIcon className="w-3.5 h-3.5 text-slate-400" />
                          <span>Section Backdrop Image</span>
                        </label>

                        <div className="flex gap-4 items-start">
                          <div className="w-20 h-20 rounded-xl border border-slate-200 overflow-hidden bg-slate-100 shrink-0 relative">
                            {sec.image ? (
                              <img src={sec.image} alt="Preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                                <ImageIcon className="w-6 h-6 stroke-1" />
                                <span className="text-[8px] font-semibold mt-1">Default</span>
                              </div>
                            )}
                          </div>

                          <div className="flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <label className="px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg text-[10px] font-bold uppercase text-slate-700 cursor-pointer transition-all shadow-3xs">
                                {uploadingImageSection === sec.id ? (
                                  <span className="flex items-center gap-1 animate-pulse text-sky-600">
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                    <span>Uploading... {personaUploadProgress}%</span>
                                  </span>
                                ) : (
                                  <span>Upload File</span>
                                )}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  disabled={uploadingImageSection !== null}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handlePersonaImageUpload(sec.id, file);
                                  }}
                                />
                              </label>

                              {sec.image && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...editingSections];
                                    delete updated[idx].image;
                                    setEditingSections(updated);
                                  }}
                                  className="p-1.5 text-rose-500 hover:bg-rose-50 border border-transparent hover:border-rose-100 rounded-lg transition-all cursor-pointer"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>

                            <div className="space-y-1">
                              <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Or enter public image URL</span>
                              <input
                                type="text"
                                placeholder="https://example.com/image.jpg"
                                value={sec.image || ''}
                                onChange={(e) => {
                                  const updated = [...editingSections];
                                  updated[idx].image = e.target.value || undefined;
                                  setEditingSections(updated);
                                }}
                                className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-[10px] focus:ring-1 focus:ring-[#0094c8] outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right Column: Interactive Quote, Person Details & Bullet Points */}
                    <div className="space-y-4">
                      
                      {/* Person Quote Box */}
                      <div className="p-4 bg-sky-50/30 border border-sky-100 rounded-2xl space-y-3 text-left">
                        <div className="text-[10px] font-extrabold text-sky-700 uppercase tracking-widest">QUOTE & TESTIMONIAL PERSONA</div>
                        
                        <div className="space-y-1.5">
                          <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Testimonial Quote</label>
                          <textarea
                            rows={2}
                            value={sec.quote}
                            onChange={(e) => {
                              const updated = [...editingSections];
                              updated[idx].quote = e.target.value;
                              setEditingSections(updated);
                            }}
                            className="w-full px-3 py-1.5 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0094c8] bg-white outline-none"
                          />
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-left">
                          <div className="space-y-1">
                            <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Initials</label>
                            <input
                              type="text"
                              maxLength={3}
                              value={sec.authorInitials}
                              onChange={(e) => {
                                const updated = [...editingSections];
                                updated[idx].authorInitials = e.target.value;
                                setEditingSections(updated);
                              }}
                              className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs font-black uppercase text-center bg-white outline-none focus:ring-1 focus:ring-[#0094c8]"
                            />
                          </div>

                          <div className="col-span-2 space-y-1">
                            <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Full Name</label>
                            <input
                              type="text"
                              value={sec.author}
                              onChange={(e) => {
                                const updated = [...editingSections];
                                updated[idx].author = e.target.value;
                                setEditingSections(updated);
                              }}
                              className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs font-bold bg-white outline-none focus:ring-1 focus:ring-[#0094c8]"
                            />
                          </div>
                        </div>

                        <div className="space-y-1 text-left">
                          <label className="text-[9px] font-bold text-slate-400 uppercase tracking-wide">Job Title / Subtitle Details</label>
                          <input
                            type="text"
                            value={sec.authorDetails}
                            onChange={(e) => {
                              const updated = [...editingSections];
                              updated[idx].authorDetails = e.target.value;
                              setEditingSections(updated);
                            }}
                            className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs bg-white outline-none focus:ring-1 focus:ring-[#0094c8]"
                          />
                        </div>
                      </div>

                      {/* Bullet Points checklist */}
                      <div className="p-4 bg-slate-50/70 border border-slate-150 rounded-2xl space-y-3 text-left">
                        <div className="text-[10px] font-extrabold text-slate-500 uppercase tracking-widest">BULLET CHECKLIST ITEMS</div>
                        
                        {[0, 1, 2].map((bulletIdx) => (
                          <div key={bulletIdx} className="space-y-1 text-left">
                            <label className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Item {bulletIdx + 1}</label>
                            <div className="flex items-center gap-2">
                              <div className="w-5 h-5 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-[10px] font-bold select-none shrink-0">
                                {bulletIdx + 1}
                              </div>
                              <input
                                type="text"
                                value={sec.items[bulletIdx] || ''}
                                onChange={(e) => {
                                  const updated = [...editingSections];
                                  const newItems = [...updated[idx].items];
                                  newItems[bulletIdx] = e.target.value;
                                  updated[idx].items = newItems;
                                  setEditingSections(updated);
                                }}
                                className="w-full px-3 py-1.5 border border-slate-200 rounded-xl text-xs bg-white focus:ring-1 focus:ring-[#0094c8] outline-none text-slate-700 font-medium"
                              />
                            </div>
                          </div>
                        ))}
                      </div>

                    </div>

                  </div>
                );
              })}

            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <button
                type="button"
                onClick={async () => {
                  const confirmed = await inAppConfirm({
                    title: "Reset Persona Layout",
                    message: "Are you sure you want to revert all changes for this region to the beautiful platform default layout?",
                    confirmText: "Reset to Defaults",
                    type: "warning",
                  });
                  if (confirmed) {
                    setEditingSections(JSON.parse(JSON.stringify(DEFAULT_PERSONA_SECTIONS)));
                    inAppToast({
                      title: "Layout Reset",
                      message: "Persona sections reset to defaults.",
                      type: "info",
                    });
                  }
                }}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-600 hover:text-slate-850 font-black text-[10px] uppercase tracking-wider rounded-xl transition-all cursor-pointer"
              >
                Reset to Defaults
              </button>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setEditingPersonaCounty(null)}
                  className="px-4 py-2 bg-white hover:bg-slate-100 border border-slate-200 text-slate-500 hover:text-slate-700 font-black text-[10px] uppercase tracking-wider rounded-xl transition-all cursor-pointer"
                >
                  Cancel
                </button>
                
                <button
                  type="button"
                  onClick={handleSavePersonaSections}
                  className="px-5 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white font-black text-[10px] uppercase tracking-wider rounded-xl transition-all shadow-sm cursor-pointer flex items-center gap-1.5"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Save Configuration</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Portal Configuration Popup Modal */}
      <PortalConfigModal
        county={activeSelectedCounty}
        isOpen={selectedPortalCounty !== null}
        onClose={() => setSelectedPortalCounty(null)}
        onUpdateTenant={updateTenantBranding}
        onUpdateAllTenants={updateAllTenantsBranding}
        onOpenPersonaEditor={(county) => setEditingPersonaCounty(county)}
      />
    </div>
  );
};

